import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Chip, 
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Collapse,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  Menu,
  Popover,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { formatCreditAmount } from '../utils/format';
import { useQuota } from '../contexts/QuotaContext';
import { SubscriptionTypes, OrganizationTypes, WorkspaceTypes, ConversationTypes, SystemEventTypes } from '@flashbacktech/flashbackclient/api';
import UpgradePlanModal from '../components/UpgradePlanModal';
import OrganizationUpgradePrompt from '../components/OrganizationUpgradePrompt';
import MultiRoleSelect from '../components/MultiRoleSelect';
import { toast } from 'react-toastify';
import AIPolicies from './AIPolicies';

// Import notification icons for Activity Logs
import checkNotifsIcon from '../assets/notifications/check-notifs.svg';
import reloadNotifsIcon from '../assets/notifications/reload-notifs.svg';
import warningNotifsIcon from '../assets/notifications/warning-notifs.svg';
import ActivationLinks from './ActivationLinks';
import StatisticsExplorer from '../components/StatisticsExplorer';

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// =============================================================================
// TODO: Backend connections to finalize (coordinate with Ravière)
// =============================================================================
// 
// 1. TEAM MEMBERS:
//    - Join/Leave organization (POST/DELETE /organization/users)
//    - Role changes (PATCH /organization/users/:id) - Already implemented
//
// 2. RECENT ACTIVITIES:
//    - By user: GET /organization/activities?userId=xxx
//    - By workspace: GET /organization/activities?workspaceId=xxx
//    - By repository: GET /organization/activities?repositoryId=xxx
//
// 3. CARDS TO CONNECT (Dashboard Stats):
//    - Top 5 repositories: GET /organization/stats/top-repositories
//    - Workflows: GET /organization/stats/workflows
//    - Credits: Already connected via getCreditsBalance/getCreditsMonthlyStats
//    - Workspace stats: GET /organization/stats/workspaces
//
// 4. USER DETAIL (Personal Statistics):
//    - Messages (not "requests"): GET /users/:id/stats?type=messages
//    - Tokens sent: GET /users/:id/stats?type=tokens
//    - Conversations: GET /users/:id/stats?type=conversations
//    - IMPORTANT: Show PERSONAL stats only, NOT global/organization stats
//
// =============================================================================

// Mock data for Statistics charts (TODO: Replace with real API data)
const mockCreditsData = [
  { name: 'Week 1', used: 2400, remaining: 7600 },
  { name: 'Week 2', used: 4200, remaining: 5800 },
  { name: 'Week 3', used: 6800, remaining: 3200 },
  { name: 'Week 4', used: 8900, remaining: 1100 },
];

const mockRequestsData = [
  { name: 'Mon', requests: 1200 },
  { name: 'Tue', requests: 1900 },
  { name: 'Wed', requests: 1400 },
  { name: 'Thu', requests: 2100 },
  { name: 'Fri', requests: 1800 },
  { name: 'Sat', requests: 800 },
  { name: 'Sun', requests: 600 },
];

const mockResourcesData = [
  { name: 'Buckets', value: 12, color: '#10B981' },
  { name: 'Repositories', value: 8, color: '#617BFF' },
];

const mockViolationsData = [
  { name: 'Week 1', logged: 5, blocked: 2 },
  { name: 'Week 2', logged: 8, blocked: 3 },
  { name: 'Week 3', logged: 3, blocked: 1 },
  { name: 'Week 4', logged: 7, blocked: 2 },
];

const mockTokensData = [
  { name: 'Jan', tokensIn: 45000, tokensOut: 32000 },
  { name: 'Feb', tokensIn: 52000, tokensOut: 38000 },
  { name: 'Mar', tokensIn: 61000, tokensOut: 45000 },
  { name: 'Apr', tokensIn: 78000, tokensOut: 56000 },
];

const mockConversationsData = [
  { name: 'Mon', conversations: 45 },
  { name: 'Tue', conversations: 62 },
  { name: 'Wed', conversations: 58 },
  { name: 'Thu', conversations: 71 },
  { name: 'Fri', conversations: 55 },
  { name: 'Sat', conversations: 28 },
  { name: 'Sun', conversations: 23 },
];

const mockModelUsageData = [
  { name: 'GPT-4', value: 65, color: '#10B981' },
  { name: 'Claude', value: 25, color: '#8B5CF6' },
  { name: 'Other', value: 10, color: '#F59E0B' },
];

const mockLatencyData = [
  { name: '00:00', latency: 1.2 },
  { name: '04:00', latency: 0.9 },
  { name: '08:00', latency: 1.8 },
  { name: '12:00', latency: 2.1 },
  { name: '16:00', latency: 1.5 },
  { name: '20:00', latency: 1.1 },
];

const mockStorageData = [
  { name: 'Jan', storage: 12 },
  { name: 'Feb', storage: 18 },
  { name: 'Mar', storage: 25 },
  { name: 'Apr', storage: 32 },
];

const mockTrafficData = [
  { name: 'Mon', upload: 5.2, download: 12.8 },
  { name: 'Tue', upload: 6.8, download: 15.2 },
  { name: 'Wed', upload: 4.5, download: 11.3 },
  { name: 'Thu', upload: 7.2, download: 18.5 },
  { name: 'Fri', upload: 5.9, download: 14.1 },
  { name: 'Sat', upload: 3.1, download: 8.2 },
  { name: 'Sun', upload: 2.5, download: 6.7 },
];

const mockBucketActivityData = [
  { name: 'Bucket 1', operations: 4500 },
  { name: 'Bucket 2', operations: 3200 },
  { name: 'Bucket 3', operations: 2800 },
  { name: 'Bucket 4', operations: 1950 },
];

const mockRepoActivityData = [
  { name: 'Indexed', value: 89, color: '#10B981' },
  { name: 'Pending', value: 11, color: '#F59E0B' },
];

function Organization() {
  const { globalData, authState, refreshGlobalData, setSelectedWorkspace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const selectedWorkspaceId = useSelectedWorkspace();
  
  // Check if user is flashback team (for Activation Links)
  const isFlashbackTeam = authState?.user?.email?.endsWith('@flashback.tech') || false;
  
  // Log when full Organization page is mounted (to confirm you're on /organization, not ProfileModal)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Organization] page mounted (path /organization)');
    }
    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Organization] page unmounted');
      }
    };
  }, []);

  // Single active section state controlled by sidebar URL params
  type SectionType = 'Overview' | 'Team' | 'Workspace' | 'Statistics' | 'Billing' | 'Activation Links' | 'Policies';
  const [activeSection, setActiveSection] = useState<SectionType>(() => {
    // Check if we should open Workspace tab on mount
    try {
      const openWorkspace = localStorage.getItem('openOrganizationWorkspaceTab');
      if (openWorkspace === '1') {
        localStorage.removeItem('openOrganizationWorkspaceTab');
        return 'Workspace';
      }
    } catch (e) {
      console.error('[Organization] Error reading openOrganizationWorkspaceTab flag:', e);
    }
    return 'Overview';
  });
  
  // Team sub-section state (overview, members, or member-details)
  type TeamSubSection = 'overview' | 'members' | 'member-details';
  const [teamSubSection, setTeamSubSection] = useState<TeamSubSection>('overview');
  
  // Workspace sub-section state (overview, management, or workspace-details)
  type WorkspaceSubSection = 'overview' | 'management' | 'workspace-details';
  const [workspaceSubSection, setWorkspaceSubSection] = useState<WorkspaceSubSection>('overview');
  
  // Policies sub-section state
  type PoliciesSubSection = 'llm-guardrails';
  const [policiesSubSection, setPoliciesSubSection] = useState<PoliciesSubSection>('llm-guardrails');
  
  // Statistics sub-section state (gateway, chat, policies, or credits)
  type StatisticsSubSection = 'gateway' | 'chat' | 'policies' | 'credits';
  const [statisticsSubSection, setStatisticsSubSection] = useState<StatisticsSubSection>('gateway');
  
  // Statistics explorer state
  const [statsExplorerWorkspace, setStatsExplorerWorkspace] = useState<string | null>(null);
  const [statsExplorerRepo, setStatsExplorerRepo] = useState<string | null>(null);
  const [statsExplorerResource, setStatsExplorerResource] = useState<string | null>(null);
  const [statsExpandedNodes, setStatsExpandedNodes] = useState<Set<string>>(new Set());
  const [statsCollapsedSections, setStatsCollapsedSections] = useState<Set<string>>(new Set());
  const [statsDateRange, setStatsDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  
  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Organization data
  const [org, setOrg] = useState<{
    id: string;
    name: string;
    domain: string;
    address1?: string;
    address2?: string;
    city?: string;
    zipcode?: string;
    state?: string;
    country?: string;
    phone?: string;
    website?: string;
    mfaEnforced?: boolean;
  } | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [companyDomain, setCompanyDomain] = useState('');
  const [companyAddress1, setCompanyAddress1] = useState('');
  const [companyAddress2, setCompanyAddress2] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyZipcode, setCompanyZipcode] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [companyCountry, setCompanyCountry] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyMfaEnforced, setCompanyMfaEnforced] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [canEditCompany, setCanEditCompany] = useState<boolean>(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  
  // Team data
  const [team, setTeam] = useState<OrganizationTypes.OrgUserResponse[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string>('');
  const [canManageUsers, setCanManageUsers] = useState(true);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<number>(0);
  
  // Team invite states
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteRole, setInviteRole] = useState<number>(0);
  const [inviteAuthMethod, setInviteAuthMethod] = useState<'LOCAL' | 'GOOGLE' | 'GITHUB'>('LOCAL');
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);

  // Validate duplicate email on input
  useEffect(() => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      setInviteEmailError(null);
      return;
    }
    const exists = team.some((m) => ((m as any).email || '').toLowerCase() === email);
    if (exists) {
      setInviteEmailError('The user already exist in the organization.');
    } else {
      setInviteEmailError(null);
    }
  }, [inviteEmail, team]);
  
  // Upgrade modal
  const [upgradePlanModalOpen, setUpgradePlanModalOpen] = useState(false);

  // Statistics filters state
  const [statsDatePickerOpen, setStatsDatePickerOpen] = useState(false);
  const [statsDateAnchorEl, setStatsDateAnchorEl] = useState<HTMLElement | null>(null);
  const [statsSelectedPreset, setStatsSelectedPreset] = useState('All-time');
  const [statsWorkspaceFilter, setStatsWorkspaceFilter] = useState('all');
  const [statsWorkspaceAnchorEl, setStatsWorkspaceAnchorEl] = useState<HTMLElement | null>(null);
  const [statsUserFilter, setStatsUserFilter] = useState('all');
  const [statsUserAnchorEl, setStatsUserAnchorEl] = useState<HTMLElement | null>(null);
  const [statsResourceType, setStatsResourceType] = useState<'all' | 'storage' | 'repos' | 'services'>('all');
  
  // Repository selection for Statistics
  const [statsRepositories, setStatsRepositories] = useState<any[]>([]);
  const [statsSelectedRepos, setStatsSelectedRepos] = useState<Set<string>>(new Set());
  const [statsRepoAnchorEl, setStatsRepoAnchorEl] = useState<HTMLElement | null>(null);
  const [statsReposLoading, setStatsReposLoading] = useState(false);
  const [statsRepoFilterOpen, setStatsRepoFilterOpen] = useState(false);

  // Gateway Statistics filters - new dropdowns
  const [gatewayRepoAnchorEl, setGatewayRepoAnchorEl] = useState<HTMLElement | null>(null);
  const [gatewaySelectedRepos, setGatewaySelectedRepos] = useState<Set<string>>(new Set());
  const [gatewayResourcesAnchorEl, setGatewayResourcesAnchorEl] = useState<HTMLElement | null>(null);
  const [gatewayResourceCategory, setGatewayResourceCategory] = useState<'all' | 'storage' | 'ai-llm'>('all');
  const [gatewaySelectedBuckets, setGatewaySelectedBuckets] = useState<Set<string>>(new Set());
  const [gatewaySelectedAiResources, setGatewaySelectedAiResources] = useState<Set<string>>(new Set());
  const [gatewayApiKeysAnchorEl, setGatewayApiKeysAnchorEl] = useState<HTMLElement | null>(null);
  const [gatewaySelectedApiKeys, setGatewaySelectedApiKeys] = useState<Set<string>>(new Set());
  const [gatewayBuckets, setGatewayBuckets] = useState<any[]>([]);
  const [gatewayApiKeys, setGatewayApiKeys] = useState<any[]>([]);

  // Private Chat Statistics filters
  const [chatWorkspaceFilter, setChatWorkspaceFilter] = useState('all');
  const [chatWorkspaceAnchorEl, setChatWorkspaceAnchorEl] = useState<HTMLElement | null>(null);
  const [chatRepoAnchorEl, setChatRepoAnchorEl] = useState<HTMLElement | null>(null);
  const [chatSelectedRepos, setChatSelectedRepos] = useState<Set<string>>(new Set());
  const [chatUserFilter, setChatUserFilter] = useState('all');
  const [chatUserAnchorEl, setChatUserAnchorEl] = useState<HTMLElement | null>(null);

  // AI LLM Stats state (V2 shape from getAiGatewayBreakdown)
  interface AILLMStats {
    aiLlmId: string;
    requests: number;
    tokensIn: number;
    tokensOut: number;
  }
  const [aiLlmStats, setAiLlmStats] = useState<AILLMStats[]>([]);
  const [aiLlmStatsLoading, setAiLlmStatsLoading] = useState(false);

  // Workspace editing (inline)
  const [editingWorkspaceId, setEditingWorkspaceId] = useState<string | null>(null);
  const [editingWorkspaceName, setEditingWorkspaceName] = useState('');

  // Workspace details dialog (with user management)
  const [wsDetailsOpen, setWsDetailsOpen] = useState(false);
  const [wsDetailsLoading, setWsDetailsLoading] = useState(false);
  const [wsDetailsError, setWsDetailsError] = useState<string | null>(null);
  const [selectedWorkspaceForDetails, setSelectedWorkspaceForDetails] = useState<any | null>(null);
  const [wsNameEdit, setWsNameEdit] = useState('');
  const [addingEmail, setAddingEmail] = useState('');
  const [addingRole, setAddingRole] = useState<WorkspaceTypes.AccessType>(WorkspaceTypes.AccessType.READ);
  const [savingWs, setSavingWs] = useState(false);

  // Member action menu (3-dot menu)
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<null | HTMLElement>(null);
  const [memberMenuId, setMemberMenuId] = useState<string | null>(null);
  
  // Team search
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [showTeamSearch, setShowTeamSearch] = useState(false);

  // Workspace search and menu
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState('');
  const [showWorkspaceSearch, setShowWorkspaceSearch] = useState(false);
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState<null | HTMLElement>(null);
  const [workspaceMenuId, setWorkspaceMenuId] = useState<string | null>(null);
  const [addWorkspaceOpen, setAddWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  
  // Handle workspace creation
  const handleCreateWorkspace = useCallback(async () => {
    if (!newWorkspaceName.trim()) {
      toast.error('Workspace name cannot be empty');
      return;
    }

    setIsCreatingWorkspace(true);
    try {
      const result = await withTokenRefresh(() => 
        apiClient.createWorkspace({ name: newWorkspaceName.trim() })
      );

      if (result.success && result.workspace) {
        toast.success('Workspace created successfully');
        
        // Refresh global data to update workspace list
        await refreshGlobalData(true);
        
        // Automatically select the newly created workspace
        setSelectedWorkspace(result.workspace.id);
        
        // Close form and reset input
        setAddWorkspaceOpen(false);
        setNewWorkspaceName('');
      } else {
        toast.error('Failed to create workspace');
      }
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      toast.error(error?.message || 'Failed to create workspace');
    } finally {
      setIsCreatingWorkspace(false);
    }
  }, [newWorkspaceName, withTokenRefresh, apiClient, refreshGlobalData, setSelectedWorkspace]);
  
  // Enriched workspaces with users (fetched separately)
  const [enrichedWorkspaces, setEnrichedWorkspaces] = useState<any[]>([]);

  // Workspace pagination
  const [workspacePage, setWorkspacePage] = useState(0);
  const workspacesPerPage = 6;

  // Member details (full page view)
  const [selectedMember, setSelectedMember] = useState<OrganizationTypes.OrgUserResponse | null>(null);

  // Edit member role modal (popup)
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [editRoleMember, setEditRoleMember] = useState<OrganizationTypes.OrgUserResponse | null>(null);
  const [editRoleValue, setEditRoleValue] = useState<number>(0);

  // Workspace details (full page view)
  const [selectedWorkspaceDetails, setSelectedWorkspaceDetails] = useState<any>(null);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  // Workspace resources counts for member details view
  const [workspaceResourceCounts, setWorkspaceResourceCounts] = useState<Record<string, { buckets: number; repos: number; aiLlms: number }>>({});

  // Per-member activity stats (real API data)
  const [memberStats, setMemberStats] = useState<{ conversations: number; totalTokens: number; messagesSent: number } | null>(null);
  const [memberStatsLoading, setMemberStatsLoading] = useState(false);

  // Per-member activity events (system events filtered by userId)
  const [memberEvents, setMemberEvents] = useState<SystemEventTypes.SystemEventResponse[]>([]);
  const [memberEventsLoading, setMemberEventsLoading] = useState(false);

  // Helper: get icon for event type (Activity Logs)
  const getEventIcon = (event: string): string => {
    const evt = event.toLowerCase();
    if (evt === 'created') return checkNotifsIcon;
    if (evt === 'updated' || evt === 'changed') return reloadNotifsIcon;
    if (evt === 'deleted' || evt === 'invalid') return warningNotifsIcon;
    return checkNotifsIcon;
  };

  // Helper: generate readable message for events (Activity Logs)
  const getEventMessage = (context: string, event: string, jsonData?: string | null): string => {
    const ctx = context.toLowerCase();
    const evt = event.toLowerCase();
    let itemName = '';
    try {
      if (jsonData) {
        const obj = JSON.parse(jsonData);
        itemName = obj.name || '';
      }
    } catch { /* ignore */ }

    if (ctx === 'bucket') {
      if (evt === 'created') return itemName ? `Bucket "${itemName}" was successfully created` : 'Bucket was successfully created';
      if (evt === 'updated') return itemName ? `Bucket "${itemName}" was updated` : 'Bucket was updated';
      if (evt === 'deleted') return itemName ? `Bucket "${itemName}" was deleted` : 'Bucket was deleted';
    }
    if (ctx === 'repo' || ctx === 'repository') {
      if (evt === 'created') return itemName ? `Repository "${itemName}" was successfully created` : 'Repository was successfully created';
      if (evt === 'updated') return itemName ? `Repository "${itemName}" was updated` : 'Repository was updated';
      if (evt === 'deleted') return itemName ? `Repository "${itemName}" was deleted` : 'Repository was deleted';
    }
    if (ctx === 'apikey') {
      if (evt === 'created') return itemName ? `API Key "${itemName}" was created` : 'API Key was created';
      if (evt === 'updated') return itemName ? `API Key "${itemName}" was updated` : 'API Key was updated';
      if (evt === 'deleted') return itemName ? `API Key "${itemName}" was deleted` : 'API Key was deleted';
    }
    if (ctx === 'aiapikey') {
      if (evt === 'created') return itemName ? `AI API Key "${itemName}" was created` : 'AI API Key was created';
      if (evt === 'updated') return itemName ? `AI API Key "${itemName}" was updated` : 'AI API Key was updated';
      if (evt === 'deleted') return itemName ? `AI API Key "${itemName}" was deleted` : 'AI API Key was deleted';
    }
    if (ctx === 'user') {
      if (evt === 'created') return 'User account was created';
      if (evt === 'updated') return 'User account was updated';
      if (evt === 'deleted') return 'User account was deleted';
    }
    if (ctx === 'usersettings') {
      if (evt === 'updated') return 'User settings were changed';
    }
    if (ctx === 'workspace') {
      if (evt === 'created') return itemName ? `Workspace "${itemName}" was created` : 'Workspace was created';
      if (evt === 'updated') return itemName ? `Workspace "${itemName}" was updated` : 'Workspace was updated';
      if (evt === 'deleted') return itemName ? `Workspace "${itemName}" was deleted` : 'Workspace was deleted';
    }
    if (ctx === 'organization' || ctx === 'org') {
      if (evt === 'created') return 'Organization was created';
      if (evt === 'updated') return 'Organization settings were updated';
      if (evt === 'deleted') return 'Organization was deleted';
    }
    if (ctx === 'aillm') {
      if (evt === 'created') return itemName ? `AI LLM "${itemName}" was created` : 'AI LLM was created';
      if (evt === 'updated') return itemName ? `AI LLM "${itemName}" was updated` : 'AI LLM was updated';
      if (evt === 'deleted') return itemName ? `AI LLM "${itemName}" was deleted` : 'AI LLM was deleted';
    }
    // Fallback
    const contextLabel = context.charAt(0).toUpperCase() + context.slice(1);
    return `${contextLabel} was ${evt}`;
  };

  // Parse URL query to open specific sections e.g. /organization?tab=workspace, ?tab=team&section=members
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tabParam = (params.get('tab') || '').toLowerCase();
      const sectionParam = (params.get('section') || '').toLowerCase();
      
      // Map URL params to section types
      const sectionMap: Record<string, SectionType> = {
        'workspace': 'Workspace',
        'team': 'Team',
        'statistics': 'Statistics',
        'billing': 'Billing',
        'activation-links': 'Activation Links',
        'policies': 'Policies',
        'overview': 'Overview',
        '': 'Overview',
      };
      
      const newSection = sectionMap[tabParam] || 'Overview';
      if (activeSection !== newSection) {
        setActiveSection(newSection);
      }
      
      // Handle Team sub-section
      if (tabParam === 'team' && sectionParam) {
        if (sectionParam === 'members') {
          setTeamSubSection('members');
        } else {
          setTeamSubSection('overview');
        }
      }
      
      // Handle Workspace sub-section
      if (tabParam === 'workspace' && sectionParam) {
        if (sectionParam === 'management') {
          setWorkspaceSubSection('management');
        } else {
          setWorkspaceSubSection('overview');
        }
      }
      
      // Handle Policies sub-section
      if (tabParam === 'policies' && sectionParam) {
        if (sectionParam === 'llm-guardrails') {
          setPoliciesSubSection('llm-guardrails');
        }
      }
      
      // Handle Statistics sub-section
      if (tabParam === 'statistics') {
        if (sectionParam === 'chat') {
          setStatisticsSubSection('chat');
        } else if (sectionParam === 'policies') {
          setStatisticsSubSection('policies');
        } else if (sectionParam === 'credits') {
          setStatisticsSubSection('credits');
        } else {
          setStatisticsSubSection('gateway');
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [location.search]);
  
  // Check flag continuously to catch it even if component is already mounted
  useEffect(() => {
    try {
      const flag = localStorage.getItem('openOrganizationWorkspaceTab');
      if (flag === '1' && activeSection !== 'Workspace') {
        localStorage.removeItem('openOrganizationWorkspaceTab');
        setActiveSection('Workspace');
      }
    } catch (e) {
      console.error('[Organization] Error checking flag', e);
    }
  }); // No deps - runs on every render to catch the flag
  
  // Listen for event to open Workspace tab
  useEffect(() => {
    const handleOpenWorkspaceTab = () => {
      setActiveSection('Workspace');
      try {
        if (localStorage.getItem('openOrganizationWorkspaceTab') === '1') {
          localStorage.removeItem('openOrganizationWorkspaceTab');
        }
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('openOrganizationWorkspaceTab', handleOpenWorkspaceTab);
    return () => {
      window.removeEventListener('openOrganizationWorkspaceTab', handleOpenWorkspaceTab);
    };
  }, []);
  
  // Compteurs de ressources
  const [bucketsCount, setBucketsCount] = useState(0);
  const [repositoriesCount, setRepositoriesCount] = useState(0);
  const [aiLlmsCount, setAiLlmsCount] = useState(0);
  const [storageUsedBytes, setStorageUsedBytes] = useState(0);
  
  // Top 5 data for Overview
  const [topWorkspaces, setTopWorkspaces] = useState<Array<{name: string; reposCount: number; color: string}>>([]);
  const [topRepositories, setTopRepositories] = useState<Array<{name: string; bucketsCount: number; aiCount: number; color: string}>>([]);
  
  // Subscriptions
  const [subscriptions, setSubscriptions] = useState<SubscriptionTypes.SubscriptionResponse[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<SubscriptionTypes.OrgSubscriptionResponse | null>(null);
  const [payments, setPayments] = useState<SubscriptionTypes.PaymentResponse[]>([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Credits-based billing (when backend SUBSCRIPTION_SYSTEM=credits)
  const [creditsMode, setCreditsMode] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState<{
    realtimeBalance: number;
    creditBalance: number;
    pendingConsumption: number;
    breakdown: { storage: number; ai: number };
  } | null>(null);
  const [creditPacks, setCreditPacks] = useState<{ id: string; name: string; code: string; credits: number; price: number }[]>([]);
  const [creditTransactions, setCreditTransactions] = useState<Array<{
    id: string;
    creditAmount: number;
    createdAt: string;
    description: string | null;
    type: string;
    subscriptionName?: string;
    packName?: string;
  }>>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);

  // Plan Usage: paged credit transactions (when credits mode)
  const [planUsageTxPage, setPlanUsageTxPage] = useState(1);
  const [planUsageTxLimit] = useState(10);
  const [planUsageTxList, setPlanUsageTxList] = useState<Array<{
    id: string;
    creditAmount: number;
    createdAt: string;
    description: string | null;
    type: string;
    subscriptionName?: string;
    packName?: string;
  }>>([]);
  const [planUsageTxPagination, setPlanUsageTxPagination] = useState<{ total: number; page: number; limit: number } | null>(null);
  const [planUsageTxLoading, setPlanUsageTxLoading] = useState(false);

  // Monthly stats for histogram (credits mode)
  const [monthlyStats, setMonthlyStats] = useState<Array<{
    month: string;
    consumption: number;
    purchases: number;
    grants: number;
    balance: number;
  }>>([]);
  const [monthlyStatsTotals, setMonthlyStatsTotals] = useState<{
    consumption: number;
    purchases: number;
    grants: number;
    balance: number;
  } | null>(null);
  const [monthlyStatsLoading, setMonthlyStatsLoading] = useState(false);
  // Histogram toggles: which series to show
  const [histogramShowConsumption, setHistogramShowConsumption] = useState(true);
  const [histogramShowPurchases, setHistogramShowPurchases] = useState(true);
  const [histogramShowBalance, setHistogramShowBalance] = useState(true);

  // Quota data
  const [userQuota, setUserQuota] = useState<{
    subscription: any;
    quotaUsage: Array<{
      capability: {
        id: string;
        code: string;
        description: string;
        type: string;
        periodType: string;
      };
      usage: {
        current: number;
        max: number;
        percentage: number;
      };
    }>;
  } | null>(null);
  
  const defaultQuotaData = {
    storageUsed: 0,
    storageLimit: 0,
    storagePercentage: 0,
    subscriptionName: '',
    subscriptionType: '',
    displayLimit: '',
  };

  const [quotaData, setQuotaData] = useState<{
    storageUsed: number;
    storageLimit: number;
    storagePercentage: number;
    subscriptionName: string;
    subscriptionType: string;
    displayLimit: string;
  }>(() => {
    const saved = localStorage.getItem('quotaData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return {
            ...defaultQuotaData,
            ...parsed,
            subscriptionName: typeof (parsed as { subscriptionName?: unknown }).subscriptionName === 'string' ? (parsed as { subscriptionName: string }).subscriptionName : '',
            subscriptionType: typeof (parsed as { subscriptionType?: unknown }).subscriptionType === 'string' ? (parsed as { subscriptionType: string }).subscriptionType : '',
            displayLimit: typeof (parsed as { displayLimit?: unknown }).displayLimit === 'string' ? (parsed as { displayLimit: string }).displayLimit : '',
          };
        }
      } catch {
        return defaultQuotaData;
      }
    }
    return defaultQuotaData;
  });

  // Handle workspace edit
  const handleEditWorkspace = (ws: any) => {
    setEditingWorkspaceId(ws.id);
    setEditingWorkspaceName(ws.name);
  };

  const handleSaveWorkspace = async (workspaceId: string) => {
    if (!editingWorkspaceName.trim()) {
      toast.error('Workspace name cannot be empty');
      return;
    }
    try {
      const result = await withTokenRefresh(() => apiClient.updateWorkspace(workspaceId, { name: editingWorkspaceName.trim() }));
      if (result.success) {
        toast.success('Workspace updated successfully');
        setEditingWorkspaceId(null);
        setEditingWorkspaceName('');
        // Refresh workspaces
        window.dispatchEvent(new CustomEvent('workspaceChanged'));
      } else {
        toast.error('Failed to update workspace');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update workspace');
    }
  };

  const handleCancelEditWorkspace = () => {
    setEditingWorkspaceId(null);
    setEditingWorkspaceName('');
  };

  // Fetch all workspaces with their users
  const fetchEnrichedWorkspaces = async () => {
    if (!globalData.workspaces || globalData.workspaces.length === 0) {
      setEnrichedWorkspaces([]);
      return;
    }
    setLoadingWorkspaces(true);
    try {
      const enriched = await Promise.all(
        globalData.workspaces.map(async (ws: any) => {
          try {
            const res = await withTokenRefresh(() => apiClient.getWorkspace(ws.id));
            if (res.success && res.workspace) {
              return res.workspace;
            }
            return ws;
          } catch {
            return ws;
          }
        })
      );
      setEnrichedWorkspaces(enriched);
    } catch (e) {
      console.error('Failed to fetch enriched workspaces:', e);
      setEnrichedWorkspaces(globalData.workspaces || []);
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  // Fetch enriched workspaces when entering workspace management
  useEffect(() => {
    if (activeSection === 'Workspace' && workspaceSubSection === 'management') {
      fetchEnrichedWorkspaces();
    }
  }, [activeSection, workspaceSubSection, globalData.workspaces]);

  // Select workspace and fetch full details with users for workspace-details view
  const selectWorkspaceWithDetails = async (ws: any) => {
    setWorkspaceSubSection('workspace-details');
    setSelectedWorkspaceDetails(ws); // Show immediately with basic data
    try {
      const res = await withTokenRefresh(() => apiClient.getWorkspace(ws.id));
      if (res.success && res.workspace) {
        setSelectedWorkspaceDetails(res.workspace); // Update with full data including users
      }
    } catch (e) {
      console.error('Failed to fetch workspace details:', e);
    }
  };

  // Open workspace details dialog
  const openWorkspaceDetails = async (ws: any) => {
    setSelectedWorkspaceForDetails(null);
    setWsNameEdit(ws.name);
    setAddingEmail('');
    setAddingRole(WorkspaceTypes.AccessType.READ);
    setWsDetailsError(null);
    setWsDetailsLoading(true);
    setWsDetailsOpen(true);
    try {
      const res = await withTokenRefresh(() => apiClient.getWorkspace(ws.id));
      if (res.success && res.workspace) {
        setSelectedWorkspaceForDetails(res.workspace);
        setWsNameEdit(res.workspace.name);
      } else {
        setWsDetailsError('Failed to load workspace');
      }
    } catch (e: any) {
      setWsDetailsError(e?.message || 'Failed to load workspace');
    } finally {
      setWsDetailsLoading(false);
    }
  };

  const handleUpdateWsName = async () => {
    if (!selectedWorkspaceForDetails) return;
    setSavingWs(true);
    setWsDetailsError(null);
    try {
      const res = await withTokenRefresh(() => apiClient.updateWorkspace(selectedWorkspaceForDetails.id, { name: wsNameEdit.trim() }));
      if (!res.success) throw new Error('Failed to update workspace');
      const g = await withTokenRefresh(() => apiClient.getWorkspace(selectedWorkspaceForDetails.id));
      if (g.success && g.workspace) {
        setSelectedWorkspaceForDetails(g.workspace);
        setWsNameEdit(g.workspace.name);
      }
      window.dispatchEvent(new CustomEvent('workspaceChanged'));
      toast.success('Workspace updated');
    } catch (e: any) {
      setWsDetailsError(e?.message || 'Failed to update workspace');
    } finally {
      setSavingWs(false);
    }
  };

  const handleAddUserToWorkspace = async () => {
    if (!selectedWorkspaceForDetails) return;
    const email = addingEmail.trim().toLowerCase();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setWsDetailsError('Invalid email format');
      return;
    }
    setSavingWs(true);
    setWsDetailsError(null);
    try {
      const orgUsersRes = await withTokenRefresh(() => apiClient.getOrganizationUsers());
      const byEmail = new Map<string, string>();
      (orgUsersRes.data || []).forEach((u) => {
        if (u.email) byEmail.set(u.email.toLowerCase(), u.id);
      });
      const userId = byEmail.get(email);
      if (!userId) throw new Error('This email is not part of your organization');
      const res = await withTokenRefresh(() => apiClient.addUserToWorkspace(selectedWorkspaceForDetails.id, { userId, role: addingRole }));
      if (!res.success) throw new Error('Failed to add user');
      const g = await withTokenRefresh(() => apiClient.getWorkspace(selectedWorkspaceForDetails.id));
      if (g.success && g.workspace) setSelectedWorkspaceForDetails(g.workspace);
      setAddingEmail('');
      setAddingRole(WorkspaceTypes.AccessType.READ);
      toast.success('User added');
    } catch (e: any) {
      setWsDetailsError(e?.message || 'Failed to add user');
    } finally {
      setSavingWs(false);
    }
  };

  const handleRemoveUserFromWorkspace = async (userId: string) => {
    if (!selectedWorkspaceForDetails) return;
    if (!window.confirm('Remove this user from the workspace?')) return;
    setSavingWs(true);
    setWsDetailsError(null);
    try {
      const res = await withTokenRefresh(() => apiClient.removeUserFromWorkspace(selectedWorkspaceForDetails.id, userId));
      if (!res.success) throw new Error('Failed to remove user');
      const g = await withTokenRefresh(() => apiClient.getWorkspace(selectedWorkspaceForDetails.id));
      if (g.success && g.workspace) setSelectedWorkspaceForDetails(g.workspace);
      toast.success('User removed');
    } catch (e: any) {
      setWsDetailsError(e?.message || 'Failed to remove user');
    } finally {
      setSavingWs(false);
    }
  };

  // EXACT copy from OrganizationModal - Load buckets count
  const loadBucketsCount = useCallback(async () => {
    try {
      if (!authState.token) return;
      const response = await withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient));
      if (response && response.buckets && Array.isArray(response.buckets)) {
        setBucketsCount(response.buckets.length);
      } else {
        setBucketsCount(0);
      }
    } catch (error) {
      setBucketsCount(0);
    }
  }, [authState.token, withTokenRefresh, apiClient]);

  // EXACT copy from OrganizationModal - Load repositories count + top repos data
  const loadRepositoriesCount = useCallback(async () => {
    try {
      if (!authState.token) return;
      const response = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
      if (response && response.repos && Array.isArray(response.repos)) {
        const repos = response.repos;
        setRepositoriesCount(repos.length);
        
        // Count AI LLMs across all repos
        let totalAiLlms = 0;
        repos.forEach((repo: any) => {
          if (repo.aiLlms && Array.isArray(repo.aiLlms)) {
            totalAiLlms += repo.aiLlms.length;
          }
        });
        setAiLlmsCount(totalAiLlms);
        
        // Build top 5 repositories by resources (buckets + AI LLMs)
        const colors = ['#617BFF', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];
        const reposSorted = [...repos]
          .map((r: any) => ({
            name: r.name,
            bucketsCount: r.buckets?.length || 0,
            aiCount: r.aiLlms?.length || 0,
            total: (r.buckets?.length || 0) + (r.aiLlms?.length || 0),
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 5)
          .map((r, i) => ({ ...r, color: colors[i] || '#94A3B8' }));
        setTopRepositories(reposSorted);
        
        // Build top 5 workspaces by repos count
        const workspaceRepoCount: Record<string, { name: string; count: number }> = {};
        repos.forEach((repo: any) => {
          const wsId = repo.workspaceId || repo.workspace_id || repo.workspace?.id;
          const wsName = repo.workspace?.name || globalData.workspaces?.find((w: any) => w.id === wsId)?.name || `Workspace ${wsId?.slice(0, 6) || 'Unknown'}`;
          if (wsId) {
            if (!workspaceRepoCount[wsId]) {
              workspaceRepoCount[wsId] = { name: wsName, count: 0 };
            }
            workspaceRepoCount[wsId].count++;
          }
        });
        const topWs = Object.values(workspaceRepoCount)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5)
          .map((ws, i) => ({ name: ws.name, reposCount: ws.count, color: colors[i] || '#94A3B8' }));
        setTopWorkspaces(topWs);
      } else {
        setRepositoriesCount(0);
        setAiLlmsCount(0);
        setTopRepositories([]);
        setTopWorkspaces([]);
      }
    } catch (error) {
      setRepositoriesCount(0);
      setAiLlmsCount(0);
      setTopRepositories([]);
      setTopWorkspaces([]);
    }
  }, [authState.token, withTokenRefresh, apiClient, globalData.workspaces]);

  // Load workspace-specific resource counts for member details view
  const loadWorkspaceResourceCounts = useCallback(async () => {
    if (!authState?.token) return;
    try {
      const [bucketsResp, reposResp] = await Promise.all([
        withTokenRefresh(() => apiClient.getStorageBuckets()),
        withTokenRefresh(() => apiClient.getStorageRepos()),
      ]);
      
      const counts: Record<string, { buckets: number; repos: number; aiLlms: number }> = {};
      
      // Initialize counts for all workspaces
      globalData.workspaces?.forEach((ws: any) => {
        counts[ws.id] = { buckets: 0, repos: 0, aiLlms: 0 };
      });
      
      // Count buckets per workspace
      if (bucketsResp?.buckets && Array.isArray(bucketsResp.buckets)) {
        bucketsResp.buckets.forEach((bucket: any) => {
          const wsId = bucket.workspaceId || bucket.workspace_id || bucket.workspace?.id;
          if (wsId && counts[wsId]) {
            counts[wsId].buckets++;
          }
        });
      }
      
      // Count repos and AI LLMs per workspace
      if (reposResp?.repos && Array.isArray(reposResp.repos)) {
        reposResp.repos.forEach((repo: any) => {
          const wsId = repo.workspaceId || repo.workspace_id || repo.workspace?.id;
          if (wsId && counts[wsId]) {
            counts[wsId].repos++;
            counts[wsId].aiLlms += repo.aiLlms?.length || 0;
          }
        });
      }
      
      setWorkspaceResourceCounts(counts);
    } catch (error) {
      console.error('Failed to load workspace resource counts:', error);
    }
  }, [authState?.token, withTokenRefresh, apiClient, globalData.workspaces]);

  // Use QuotaContext instead of direct API call
  const { quotaData: quotaContextData } = useQuota();
  
  // Map quotaContextData to the format expected by Organization component
  React.useEffect(() => {
    if (quotaContextData.quotaUsage) {
      setQuotaData(quotaContextData.quotaUsage);
    }
  }, [quotaContextData.quotaUsage]);

  // EXACT copy from OrganizationModal - Load user quota
  const loadUserQuota = useCallback(async () => {
    if (!authState?.token) return;
    try {
      const quotaResp = await withTokenRefresh(() => apiClient.getUserQuota());
      if (quotaResp.success) {
        setUserQuota({
          subscription: quotaResp.subscription,
          quotaUsage: quotaResp.quotaUsage || [],
        });
      }
    } catch (error) { /* Ignore errors */ }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // EXACT copy from OrganizationModal - Load subscriptions
  const loadSubscriptionsData = useCallback(async () => {
    if (!authState?.token) return;
    try {
      setSubscriptionLoading(true);
      const subsResp = await withTokenRefresh(() => apiClient.getSubscriptions());
      if (subsResp.success) {
        setSubscriptions(subsResp.data || []);
      }
      const mySubResp = await withTokenRefresh(() => apiClient.getMySubscription());
      if (mySubResp.success) {
        setCurrentSubscription(mySubResp.data);
      }
    } catch (error) { /* Ignore errors */ } finally {
      setSubscriptionLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Fetch paged credit transactions for Plan Usage section (defined before loadCreditsData so it can call them)
  const loadPlanUsageTransactions = useCallback(async () => {
    console.log('[Organization] loadPlanUsageTransactions called', { hasToken: !!authState?.token, page: planUsageTxPage, limit: planUsageTxLimit });
    if (!authState?.token) {
      console.log('[Organization] loadPlanUsageTransactions: no token, returning early');
      return;
    }
    setPlanUsageTxLoading(true);
    try {
      console.log('[Organization] loadPlanUsageTransactions: calling API...');
      const resp = await withTokenRefresh(() =>
        (apiClient as any).getCreditsTransactions({ page: planUsageTxPage, limit: planUsageTxLimit })
      ) as { success: boolean; data?: typeof planUsageTxList; pagination?: { total: number; page: number; limit: number } };
      console.log('[Organization] loadPlanUsageTransactions: API response', resp);
      if (resp.success && resp.data) {
        setPlanUsageTxList(resp.data);
        setPlanUsageTxPagination(resp.pagination ?? null);
      }
    } catch (err) {
      console.error('[Organization] loadPlanUsageTransactions: error', err);
      setPlanUsageTxList([]);
      setPlanUsageTxPagination(null);
    } finally {
      setPlanUsageTxLoading(false);
    }
  }, [authState?.token, planUsageTxPage, planUsageTxLimit, withTokenRefresh, apiClient]);

  // Load monthly stats for histogram via V2 getCreditStats
  const loadMonthlyStats = useCallback(async () => {
    console.log('[Organization] loadMonthlyStats called', { hasToken: !!authState?.token });
    if (!authState?.token) {
      console.log('[Organization] loadMonthlyStats: no token, returning early');
      return;
    }
    setMonthlyStatsLoading(true);
    try {
      console.log('[Organization] loadMonthlyStats: calling V2 getCreditStats...');
      const resp = await withTokenRefresh(() =>
        apiClient.getCreditStats({ scope: 'org', window: '365d' } as any)
      ) as any;
      console.log('[Organization] loadMonthlyStats: API response', resp);
      if (resp.success && resp.series?.length > 0) {
        const monthly = resp.series.map((point: any) => {
          const d = new Date(point.ts * 1000);
          return {
            month: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            consumption: point.creditsTotal || 0,
            purchases: point.creditsPurchased || 0,
            grants: point.creditsGranted || 0,
            balance: (point.creditsPurchased || 0) + (point.creditsGranted || 0) - (point.creditsTotal || 0),
          };
        });
        setMonthlyStats(monthly);
        const totals = {
          consumption: monthly.reduce((sum: number, m: any) => sum + m.consumption, 0),
          purchases: monthly.reduce((sum: number, m: any) => sum + m.purchases, 0),
          grants: monthly.reduce((sum: number, m: any) => sum + m.grants, 0),
          balance: monthly.reduce((sum: number, m: any) => sum + m.balance, 0),
        };
        setMonthlyStatsTotals(totals);
      }
    } catch (err) {
      console.error('[Organization] loadMonthlyStats: error', err);
      setMonthlyStats([]);
      setMonthlyStatsTotals(null);
    } finally {
      setMonthlyStatsLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Load credits data when in credits-based billing mode (balance + packs + transactions + plan-usage list + monthly stats in one moment)
  const loadCreditsData = useCallback(async () => {
    console.log('[Organization] loadCreditsData called', { hasToken: !!authState?.token });
    if (!authState?.token) return;
    try {
      console.log('[Organization] loadCreditsData: calling getCreditsBalance...');
      const balanceResp = await withTokenRefresh(() => (apiClient as any).getCreditsBalance()) as { success: boolean; data?: { realtimeBalance: number; creditBalance: number; pendingConsumption: number; breakdown: { storage: number; ai: number } } };
      console.log('[Organization] loadCreditsData: balance response', balanceResp);
      if (!balanceResp.success || !balanceResp.data) {
        setCreditsMode(false);
        return;
      }
      setCreditsMode(true);
      setCreditsBalance({
        realtimeBalance: balanceResp.data.realtimeBalance,
        creditBalance: balanceResp.data.creditBalance,
        pendingConsumption: balanceResp.data.pendingConsumption,
        breakdown: balanceResp.data.breakdown,
      });
      // Call transactions and stats/monthly in the same moment as balance (same entry point)
      console.log('[Organization] loadCreditsData: calling loadPlanUsageTransactions + loadMonthlyStats...');
      loadPlanUsageTransactions();
      loadMonthlyStats();
      const [packsResp, txResp] = await Promise.all([
        withTokenRefresh(() => (apiClient as any).getCreditsPacks()) as Promise<{ success: boolean; data?: { id: string; name: string; code: string; credits: number; price: number }[] }>,
        withTokenRefresh(() => (apiClient as any).getCreditsTransactions({ limit: 20, page: 1 })) as Promise<{ success: boolean; data?: Array<{ id: string; creditAmount: number; createdAt: string; description: string | null; type: string }> }>,
      ]);
      if (packsResp.success && packsResp.data) setCreditPacks(packsResp.data);
      if (txResp.success && txResp.data) setCreditTransactions(txResp.data);
    } catch (err) {
      console.error('[Organization] loadCreditsData: error', err);
      setCreditsMode(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient, loadPlanUsageTransactions, loadMonthlyStats]);

  const handleBuyCreditsPack = useCallback(async (packId: string) => {
    try {
      const response = await withTokenRefresh(() => (apiClient as any).buyCreditsPack({ packId })) as { success: boolean; checkoutUrl?: string | null; message?: string };
      if (response.success && response.checkoutUrl) {
        window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
      } else if (response.success) {
        toast.success('Credit pack purchased.');
        loadCreditsData();
      } else {
        toast.error(response.message || 'Failed to start checkout.');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to start checkout.');
    }
  }, [withTokenRefresh, apiClient, loadCreditsData]);

  // Reload transactions when page changes
  useEffect(() => {
    if (planUsageTxPage > 1) loadPlanUsageTransactions();
  }, [planUsageTxPage, loadPlanUsageTransactions]);

  const loadUsersWithRoles = async () => {
    const res = await withTokenRefresh(() => apiClient.getOrganizationUsers());
    if (res?.success && Array.isArray(res.data)) {
      return res.data;
    }
    return [];
  };

  // EXACT copy from OrganizationModal - Load organization data
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use globalData.userProfile instead of API call
        const orgId = globalData?.userProfile?.orgId;
        if (!mounted) return;
        if (!orgId) {
          setError('No organization found for this account.');
          setOrg(null);
          setTeam([]);
          return;
        }
        
        // Use globalData.organization instead of API call
        const orgData = globalData?.organization;
        // Always fetch fresh org data from API to get all fields
        const orgResp = await withTokenRefresh(() => apiClient.getOrganization(orgId));
        if (orgResp.success && orgResp.data) {
          const d = orgResp.data as any;
          const orgObj = {
            id: orgId,
            name: d.name || '',
            domain: d.domain || '',
            address1: d.address1 || '',
            address2: d.address2 || '',
            city: d.city || '',
            zipcode: d.zipcode || '',
            state: d.state || '',
            country: d.country || '',
            phone: d.phone || '',
            website: d.website || '',
            mfaEnforced: d.mfaEnforced === true,
          };
          setOrg(orgObj);
          // Initialize company edit fields
          setCompanyName(orgObj.name);
          setCompanyDomain(orgObj.domain);
          setCompanyAddress1(orgObj.address1);
          setCompanyAddress2(orgObj.address2);
          setCompanyCity(orgObj.city);
          setCompanyZipcode(orgObj.zipcode);
          setCompanyState(orgObj.state);
          setCompanyCountry(orgObj.country);
          setCompanyWebsite(orgObj.website);
          setCompanyMfaEnforced(orgObj.mfaEnforced);
          // Parse phone: extract country code if present
          if (orgObj.phone) {
            const phoneMatch = orgObj.phone.match(/^(\+\d{1,4})(.*)/);
            if (phoneMatch) {
              setPhoneCountryCode(phoneMatch[1]);
              setCompanyPhone(phoneMatch[2]);
            } else {
              setCompanyPhone(orgObj.phone);
            }
          }
        } else if (orgData && orgData.id === orgId) {
          // Fallback to globalData if API fails
          setOrg({
            id: orgId,
            name: orgData.name || '',
            domain: orgData.domain || '',
          });
          setCompanyName(orgData?.name || '');
          setCompanyDomain(orgData?.domain || '');
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to load organization');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [globalData?.userProfile?.orgId, authState?.token, withTokenRefresh, apiClient]);

  // Load counts (quota comes from QuotaContext)
  useEffect(() => {
    if (authState.token) {
      loadBucketsCount();
      loadRepositoriesCount();
    }
  }, [authState.token, loadBucketsCount, loadRepositoriesCount]);

  // Load user quota, subscriptions, and credits (balance + transactions + stats/monthly) when Billing section is active.
  // Depend on authState?.token so we re-run when token becomes available (e.g. after auth hydrate).
  useEffect(() => {
    console.log('[Organization] Billing effect', { activeSection, hasToken: !!authState?.token });
    if (activeSection !== 'Billing') {
      console.log('[Organization] Billing effect: not Billing section, skipping');
      return;
    }
    console.log('[Organization] Billing effect: calling loaders...');
    loadUserQuota();
    loadSubscriptionsData();
    setCreditsLoading(true);
    loadCreditsData().finally(() => setCreditsLoading(false));
  }, [activeSection, authState?.token, loadUserQuota, loadSubscriptionsData, loadCreditsData]);

  // EXACT copy from OrganizationModal - Lazy-load team when switching to Team or Statistics tab
  useEffect(() => {
    let mounted = true;
    const loadTeam = async () => {
      if ((activeSection !== 'Overview' && activeSection !== 'Team' && activeSection !== 'Statistics') || !org) return;
      try {
        setTeamError('');
        setTeamLoading(true);
        const usersWithRoles = await loadUsersWithRoles();
        if (!mounted) return;
        if (usersWithRoles.length > 0) {
          setTeam(usersWithRoles);
          setCanManageUsers(true);
        } else {
          setTeamError('No users found or failed to load team');
        }
      } catch (e: any) {
        if (!mounted) return;
        if (e?.status === 403) {
          setCanManageUsers(false);
          setTeamError(
            'Insufficient permissions to view team members (requires ADMINISTRATOR role or higher).'
          );
        } else {
          setTeamError(e?.message || 'Failed to load team');
        }
      } finally {
        if (mounted) setTeamLoading(false);
      }
    };
    loadTeam();
    return () => {
      mounted = false;
    };
  }, [activeSection, org]);

  // Load workspace resource counts when entering member details
  useEffect(() => {
    if (teamSubSection === 'member-details' && selectedMember) {
      loadWorkspaceResourceCounts();
    }
  }, [teamSubSection, selectedMember, loadWorkspaceResourceCounts]);

  // Load per-member activity stats (conversations + AI tokens/messages) from real API
  useEffect(() => {
    let mounted = true;
    const loadMemberStats = async () => {
      if (teamSubSection !== 'member-details' || !selectedMember || !authState?.token) return;
      const memberId = selectedMember.id;
      if (!memberId) return;

      setMemberStatsLoading(true);
      setMemberStats(null);

      let conversations = 0;
      let totalTokens = 0;
      let messagesSent = 0;

      console.log('[Organization] Loading member stats for:', { memberId, email: selectedMember.email, name: selectedMember.name });

      try {
        // 1. Get conversation count for this user
        const convParams: ConversationTypes.GetConversationsRequest = {
          take: 200,
          userId: memberId,
          showDeleted: false,
        };
        const convResponse = await withTokenRefresh(() => apiClient.getConversations(convParams));
        if (mounted) {
          conversations = convResponse.conversations?.length || 0;
          console.log('[Organization] Member conversations:', conversations);
        }
      } catch (e) {
        console.warn('[Organization] Failed to load member conversations:', e);
      }

      try {
        // 2. Get AI stats (tokens + requests) for this user - last 30 days via V2
        const aiResponse = await withTokenRefresh(() =>
          apiClient.getAiGatewayStats({ scope: 'org', window: '30d' } as any)
        ) as any;
        console.log('[Organization] AI stats (V2):', { success: aiResponse?.success, seriesLen: aiResponse?.series?.length || 0 });

        if (mounted && aiResponse?.success && aiResponse.series?.length) {
          totalTokens = aiResponse.series.reduce((sum: number, point: any) =>
            sum + (point.tokensIn ?? 0) + (point.tokensOut ?? 0), 0);
          messagesSent = aiResponse.series.reduce((sum: number, point: any) =>
            sum + (point.requests ?? 0), 0);
          console.log('[Organization] Member AI totals:', { totalTokens, messagesSent });
        }
      } catch (e) {
        console.warn('[Organization] Failed to load member AI stats:', e);
      }

      if (mounted) {
        setMemberStats({ conversations, totalTokens, messagesSent });
        setMemberStatsLoading(false);
      }
    };
    loadMemberStats();
    return () => { mounted = false; };
  }, [teamSubSection, selectedMember, authState?.token, withTokenRefresh, apiClient]);

  // Load per-member activity events (system events filtered by userId)
  useEffect(() => {
    let mounted = true;
    const loadMemberEvents = async () => {
      if (teamSubSection !== 'member-details' || !selectedMember || !authState?.token) return;
      const memberId = selectedMember.id;
      if (!memberId) return;

      setMemberEventsLoading(true);
      setMemberEvents([]);

      try {
        const request: SystemEventTypes.SystemEventQueryRequest = {
          skip: 0,
          take: 100,
          userId: memberId,
        } as any;
        const res = await withTokenRefresh(() => apiClient.getSystemEvents(request));
        if (mounted && res && Array.isArray(res.events)) {
          setMemberEvents(res.events);
        }
      } catch (e) {
        console.warn('[Organization] Failed to load member events:', e);
      } finally {
        if (mounted) setMemberEventsLoading(false);
      }
    };
    loadMemberEvents();
    return () => { mounted = false; };
  }, [teamSubSection, selectedMember, authState?.token, withTokenRefresh, apiClient]);

  // Load payments when Billing section is active
  useEffect(() => {
    let mounted = true;
    const loadPaymentsData = async () => {
      if (activeSection !== 'Billing' || !authState?.token) return;
      try {
        setPaymentsLoading(true);
        const paymentsResp = await withTokenRefresh(() => apiClient.getPayments());
        if (mounted && paymentsResp.success) {
          setPayments(paymentsResp.data || []);
        }
      } catch (error) { /* Ignore errors */ } finally {
        if (mounted) setPaymentsLoading(false);
      }
    };
    loadPaymentsData();
    return () => {
      mounted = false;
    };
  }, [activeSection, authState?.token, withTokenRefresh, apiClient]);

  // Load ALL repositories when Statistics section is active (unfiltered, for workspace tree)
  useEffect(() => {
    const loadStatsRepositories = async () => {
      if (activeSection !== 'Statistics' || !authState?.token) return;
      try {
        setStatsReposLoading(true);
        // Load ALL repos (no workspace filter) so explorer tree can group by workspace
        const response = await withTokenRefresh(() => apiClient.getStorageRepos());
        if (response.success && response.repos) {
          setStatsRepositories(response.repos);
        }
      } catch (error) { /* Ignore errors */ } finally {
        setStatsReposLoading(false);
      }
    };
    loadStatsRepositories();
  }, [activeSection, authState?.token, withTokenRefresh, apiClient]);

  // Toggle repository selection
  const toggleStatsRepo = (repoId: string) => {
    setStatsSelectedRepos((prev) => {
      const next = new Set(prev);
      if (next.has(repoId)) {
        next.delete(repoId);
      } else {
        next.add(repoId);
      }
      return next;
    });
  };

  // Select all repositories
  const selectAllStatsRepos = () => {
    setStatsSelectedRepos(new Set(statsRepositories.map((r: any) => r.id)));
  };

  // Clear repository selection
  const clearStatsRepoSelection = () => {
    setStatsSelectedRepos(new Set());
  };

  // Load buckets and API keys for Gateway filters
  useEffect(() => {
    let mounted = true;
    const loadGatewayResources = async () => {
      if (activeSection !== 'Statistics' || !authState?.token) return;
      try {
        // Load buckets
        const bucketsResponse = await withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient));
        if (mounted && bucketsResponse?.buckets) {
          let buckets = bucketsResponse.buckets;
          if (statsWorkspaceFilter && statsWorkspaceFilter !== 'all') {
            buckets = buckets.filter((b: any) => {
              const wid = b.workspaceId || b.workspace_id || b.workspace?.id || b.workspace;
              return String(wid) === String(statsWorkspaceFilter);
            });
          }
          setGatewayBuckets(buckets);
        }
        // Load API keys - use fetch directly
        const keysResponse = (await withTokenRefresh(() => apiClient.getOrgKeys(org?.id || ''))) as any;
        if (mounted && keysResponse.success && keysResponse.data) {
          setGatewayApiKeys(keysResponse.data);
        }
      } catch (error) {
        console.error('Failed to load gateway resources:', error);
      }
    };
    loadGatewayResources();
    return () => { mounted = false; };
  }, [activeSection, authState?.token, statsWorkspaceFilter, withTokenRefresh, apiClient]);

  // Load AI LLM Stats when Statistics > Chat section is active (V2)
  useEffect(() => {
    let mounted = true;
    const loadAiLlmStats = async () => {
      if (activeSection !== 'Statistics' || !authState?.token) return;
      try {
        setAiLlmStatsLoading(true);
        const response = await withTokenRefresh(() =>
          apiClient.getAiGatewayBreakdown('models', { scope: 'org', window: '30d' } as any)
        ) as any;
        if (mounted && response?.entities) {
          const mapped: AILLMStats[] = response.entities.map((entity: any) => ({
            aiLlmId: entity.entityId,
            requests: entity.totals?.requests || 0,
            tokensIn: entity.totals?.tokensIn || 0,
            tokensOut: entity.totals?.tokensOut || 0,
          }));
          setAiLlmStats(mapped);
        }
      } catch (error) {
        console.error('[Organization] Failed to load AI LLM stats:', error);
      } finally {
        if (mounted) setAiLlmStatsLoading(false);
      }
    };
    loadAiLlmStats();
    return () => { mounted = false; };
  }, [activeSection, authState?.token, withTokenRefresh, apiClient]);

  // Aggregate AI LLM stats helper (V2)
  const getAggregatedAiStats = () => {
    if (aiLlmStats.length === 0) {
      return {
        totalApiCalls: 0,
        totalTokensIn: 0,
        totalTokensOut: 0,
        totalLlmTokensIn: 0,
        totalPolicyViolations: 0,
        totalAlerts: 0,
        totalBlocks: 0,
      };
    }
    return aiLlmStats.reduce((acc, stat) => ({
      totalApiCalls: acc.totalApiCalls + (stat.requests || 0),
      totalTokensIn: acc.totalTokensIn + (stat.tokensIn || 0),
      totalTokensOut: acc.totalTokensOut + (stat.tokensOut || 0),
      totalLlmTokensIn: acc.totalLlmTokensIn,
      totalPolicyViolations: acc.totalPolicyViolations,
      totalAlerts: acc.totalAlerts,
      totalBlocks: acc.totalBlocks,
    }), {
      totalApiCalls: 0,
      totalTokensIn: 0,
      totalTokensOut: 0,
      totalLlmTokensIn: 0,
      totalPolicyViolations: 0,
      totalAlerts: 0,
      totalBlocks: 0,
    });
  };

  // Format number helper for stats display
  const formatStatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Toggle functions for Gateway filters
  const toggleGatewayRepo = (repoId: string) => {
    setGatewaySelectedRepos((prev) => {
      const next = new Set(prev);
      if (next.has(repoId)) next.delete(repoId);
      else next.add(repoId);
      return next;
    });
  };

  const toggleGatewayBucket = (bucketId: string) => {
    setGatewaySelectedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucketId)) next.delete(bucketId);
      else next.add(bucketId);
      return next;
    });
  };

  const toggleGatewayApiKey = (keyId: string) => {
    setGatewaySelectedApiKeys((prev) => {
      const next = new Set(prev);
      if (next.has(keyId)) next.delete(keyId);
      else next.add(keyId);
      return next;
    });
  };

  const toggleChatRepo = (repoId: string) => {
    setChatSelectedRepos((prev) => {
      const next = new Set(prev);
      if (next.has(repoId)) next.delete(repoId);
      else next.add(repoId);
      return next;
    });
  };

  // Get selected items summary for display
  const getGatewaySelectionSummary = () => {
    const items: string[] = [];
    if (statsWorkspaceFilter !== 'all') {
      const ws = globalData.workspaces?.find((w: any) => w.id === statsWorkspaceFilter);
      if (ws) items.push(`Workspace: ${ws.name}`);
    }
    if (gatewaySelectedRepos.size > 0) items.push(`${gatewaySelectedRepos.size} repo(s)`);
    if (gatewaySelectedBuckets.size > 0) items.push(`${gatewaySelectedBuckets.size} bucket(s)`);
    if (gatewaySelectedApiKeys.size > 0) items.push(`${gatewaySelectedApiKeys.size} API key(s)`);
    if (statsUserFilter !== 'all') {
      const user = team.find((m: any) => m.id === statsUserFilter);
      if (user) items.push(`User: ${user.name}`);
    }
    return items;
  };

  const getChatSelectionSummary = () => {
    const items: string[] = [];
    if (chatWorkspaceFilter !== 'all') {
      const ws = globalData.workspaces?.find((w: any) => w.id === chatWorkspaceFilter);
      if (ws) items.push(`Workspace: ${ws.name}`);
    }
    if (chatSelectedRepos.size > 0) items.push(`${chatSelectedRepos.size} repo(s)`);
    if (chatUserFilter !== 'all') {
      const user = team.find((m: any) => m.id === chatUserFilter);
      if (user) items.push(`User: ${user.name}`);
    }
    return items;
  };

  const handleSaveCompany = async () => {
    if (!org?.id || !canEditCompany) return;
    setCompanyLoading(true);
    setCompanyError(null);
    try {
      const fullPhone = companyPhone.trim() ? `${phoneCountryCode}${companyPhone.trim()}` : undefined;
      const body: any = {
        name: companyName.trim() || undefined,
        domain: companyDomain.trim() || undefined,
        address1: companyAddress1.trim() || undefined,
        address2: companyAddress2.trim() || undefined,
        city: companyCity.trim() || undefined,
        zipcode: companyZipcode.trim() || undefined,
        state: companyState.trim() || undefined,
        country: companyCountry.trim() || undefined,
        phone: fullPhone,
        website: companyWebsite.trim() || undefined,
        mfaEnforced: companyMfaEnforced,
      };
      const resp = await withTokenRefresh(() => apiClient.updateOrganization(org.id, body));
      if (resp.success && resp.data) {
        const d = resp.data as any;
        const orgObj = {
          id: org.id,
          name: d.name || '',
          domain: d.domain || '',
          address1: d.address1 || '',
          address2: d.address2 || '',
          city: d.city || '',
          zipcode: d.zipcode || '',
          state: d.state || '',
          country: d.country || '',
          phone: d.phone || '',
          website: d.website || '',
          mfaEnforced: d.mfaEnforced === true,
        };
        setOrg(orgObj);
        setCompanyName(orgObj.name);
        setCompanyDomain(orgObj.domain);
        setCompanyAddress1(orgObj.address1);
        setCompanyAddress2(orgObj.address2);
        setCompanyCity(orgObj.city);
        setCompanyZipcode(orgObj.zipcode);
        setCompanyState(orgObj.state);
        setCompanyCountry(orgObj.country);
        setCompanyWebsite(orgObj.website);
        setCompanyMfaEnforced(orgObj.mfaEnforced);
        if (orgObj.phone) {
          const phoneMatch = orgObj.phone.match(/^(\+\d{1,4})(.*)/);
          if (phoneMatch) {
            setPhoneCountryCode(phoneMatch[1]);
            setCompanyPhone(phoneMatch[2]);
          } else {
            setCompanyPhone(orgObj.phone);
          }
        }
        toast.success('Organization updated successfully');
      } else {
        throw new Error(resp.message || 'Failed to update organization');
      }
    } catch (e: any) {
      setCompanyError(e?.message || 'Failed to update organization');
      toast.error(e?.message || 'Failed to update organization');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      const response = await withTokenRefresh(() => apiClient.resendTeamUserInvitation(userId));
      if (response.success) {
        toast.success('Invitation email resent successfully');

      } else {
        setTeamError('Failed to resend invitation');
      }
    } catch (e: any) {
      setTeamError('Failed to resend invitation');
    } 
  };

  const getQuotaByCode = (code: string) => {
    return userQuota?.quotaUsage?.find((q) => q.capability.code === code);
  };

  // EXACT copy from OrganizationModal - Format price
  const formatPrice = (price: number, currency = 'USD'): string => {
    const symbol = currency.toUpperCase() === 'USD' ? '$' : currency;
    return `${symbol}${price}`;
  };

  // EXACT copy from OrganizationModal - Format payment status
  const formatPaymentStatus = (status: string) => {
    const config: Record<string, { label: string; bgcolor: string; color: string }> = {
      COMPLETED: { label: 'Paid', bgcolor: '#EAF8F1', color: '#1F8F6B' },
      FAILED: { label: 'Failed', bgcolor: '#FEE2E2', color: '#EF4444' },
      EXPIRED: { label: 'Expired', bgcolor: '#FEF3C7', color: '#F59E0B' },
      CANCELLED: { label: 'Cancelled', bgcolor: '#F3F4F6', color: '#6B7280' },
      PENDING: { label: 'Pending', bgcolor: '#FFF7E6', color: '#C38A00' },
    };
    return config[status] || config['PENDING'];
  };

  // EXACT copy from OrganizationModal - Open billing portal
  const handleOpenBillingPortal = async () => {
    try {
      const response = await withTokenRefresh(() => apiClient.createBillingPortal());
      if (response.success && response.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to open billing portal');
    }
  };

  // EXACT copy from OrganizationModal - Cancel pending payment
  const handleCancelPendingPayment = async () => {
    if (!window.confirm('Are you sure you want to cancel this pending payment?')) return;
    try {
      setPaymentsLoading(true);
      const response = await withTokenRefresh(() => apiClient.cancelPendingPayment());
      if (response.success) {
        const paymentsResp = await withTokenRefresh(() => apiClient.getPayments());
        if (paymentsResp.success) {
          setPayments(paymentsResp.data || []);
        }
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to cancel pending payment');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // EXACT copy from OrganizationModal - Navigate to checkout
  const handleNavigateToCheckout = async () => {
    try {
      setPaymentsLoading(true);
      const response = await withTokenRefresh(() => apiClient.getPendingPayment());
      if (response.success && response.data?.checkoutUrl) {
        window.open(response.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to get checkout URL. Please try again.');
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to get checkout URL');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // EXACT copy from OrganizationModal - Invite handler
  const handleInvite = async () => {
    // Validate required fields - password is only required for LOCAL auth
    if (!inviteEmail || !inviteFirstName || !inviteLastName) return;

    // Pre-check duplicate
    const email = inviteEmail.trim().toLowerCase();
    const exists = team.some((m) => ((m as any).email || '').toLowerCase() === email);
    if (exists) {
      setInviteEmailError('The user already exist in the organization.');
      try { toast.error('The user already exist in the organization.'); } catch (e) { /* ignore */ }
      return;
    }
    
    setTeamLoading(true);
    setTeamError('');
    try {
      const res: any = await withTokenRefresh(() =>
        apiClient.createOrganizationUser({
          email: inviteEmail,
          password: invitePassword,
          firstName: inviteFirstName,
          lastName: inviteLastName,
          orgRole: inviteRole,
          authMethod: inviteAuthMethod,
        })
      );
      if (res.success) {
        setInviteEmail('');
        setInviteFirstName('');
        setInviteLastName('');
        setInvitePassword('');
        setInviteRole(0);
        setInviteAuthMethod('LOCAL');
        setInviteEmailError(null);
        setAddMemberOpen(false);
        const users = await loadUsersWithRoles();
        setTeam(users);
      } else {
        const msg = (res.message || '').toString();
        const duplicate = msg.toLowerCase().includes('already') && msg.toLowerCase().includes('exist');
        const finalMsg = duplicate ? 'The user already exist in the organization.' : (msg || 'Failed to invite user');
        setTeamError(finalMsg);
        try { toast.error(finalMsg); } catch (e) { /* ignore */ }
      }
    } catch (e: any) {
      const msg = (e?.message || '').toString();
      const duplicate = msg.toLowerCase().includes('already') && msg.toLowerCase().includes('exist');
      const finalMsg = duplicate ? 'The user already exist in the organization.' : (msg || 'Failed to invite user');
      setTeamError(finalMsg);
      try { toast.error(finalMsg); } catch (err) { /* ignore */ }
    } finally {
      setTeamLoading(false);
    }
  };

  // EXACT copy from OrganizationModal - Delete member
  const handleDeleteMember = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    setTeamLoading(true);
    setTeamError('');
    try {
      const res: any = await withTokenRefresh(() =>
        apiClient.deleteOrganizationUser(userId)
      );
      if (res.success) {
        const users = await loadUsersWithRoles();
        setTeam(users);
      } else {
        setTeamError(res.message || 'Failed to remove member');
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to remove member');
    } finally {
      setTeamLoading(false);
    }
  };

  // EXACT copy from OrganizationModal - handleDelete (alias for handleDeleteMember)
  const handleDelete = async (userId: string) => {
    try {
      setTeamLoading(true);
      const res = await withTokenRefresh(() =>
        apiClient.deleteOrganizationUser(userId)
      );
      if (res?.success) {
        const users = await loadUsersWithRoles();
        setTeam(users);
      } else {
        setTeamError(res?.message || 'Failed to remove user');
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to remove user');
    } finally {
      setTeamLoading(false);
    }
  };

  // EXACT copy from OrganizationModal - Edit role
  const handleEditRole = (memberId: string, currentRole: number) => {
    setEditingMemberId(memberId);
    setEditingRole(currentRole);
  };

  // EXACT copy from OrganizationModal - Save role
  const handleSaveRole = async (userId: string) => {
    try {
      setTeamLoading(true);
      const updateUserData: OrganizationTypes.UpdateOrgUserRequest = {
        orgRole: editingRole,
      };
      const res = await withTokenRefresh(() => apiClient.updateOrganizationUser(userId, updateUserData));
      if (res.success) {
        // For now, just refresh the team list
        const users = await loadUsersWithRoles();
        setTeam(users);
        setEditingMemberId(null);
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to update role');
    } finally {
      setTeamLoading(false);
    }
  };

  // Get role names from bitfield - roles work as flags, not single values
  const getRoleName = (role: number | undefined): string => {
    if (role === undefined) return 'UNKNOWN';
    
    // Special cases: OWNER and ADMINISTRATOR are exclusive
    if (role === 0xff) return 'OWNER';
    if (role === 0xfe) return 'ADMINISTRATOR';
    
    // If role is 0, it's just USER
    if (role === 0) return 'USER';
    
    // Decode bitfield: check which role bits are set
    const roles: string[] = [];
    
    if (role & 0x01) roles.push('BILLING');
    if (role & 0x02) roles.push('WORKSPACES');
    
    // If no standard roles found but value is non-zero, it's unknown
    if (roles.length === 0) return 'UNKNOWN';
    
    return roles.join(', ');
  };

  // Show upgrade prompt for individual accounts
  if (!globalData?.hasOrgAccess) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        <OrganizationUpgradePrompt
          title="Organization"
          description="Centralize users, permissions, and AI configurations to securely operate AI across your organization."
          featureName="Organization"
          features={[
            {
              title: 'Workspace & Team Management',
              description: 'Create and manage organizations, workspaces, and teams with clear ownership, role separation, and scalable collaboration across departments.',
            },
            {
              title: 'Role-Based Access Control (RBAC)',
              description: 'Define fine-grained permissions for admins, developers, and users — controlling access to models, policies, data, and features at the organization level.',
            },
            {
              title: 'Centralized Policy & Configuration Control',
              description: 'Manage AI models, system prompts, safety policies, compliance rules, and usage limits from a single control plane, enforced consistently across all users and chats.',
            },
            {
              title: 'Security, Auditability & Governance',
              description: 'Organization-wide visibility with audit logs, usage tracking, and governance controls to support compliance, security reviews, and internal accountability.',
            },
          ]}
        />
      </div>
    );
  }

  return (
    <>
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Hub-like Header */}
      <div style={{ padding: '24px 40px 24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#1F2937',
              marginBottom: '8px',
              fontFamily: 'Inter',
              letterSpacing: '-0.02em',
            }}
          >
            {activeSection === 'Overview' ? 'Home' : activeSection}
          </Typography>
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: '#6B7280',
              fontFamily: 'Inter',
              lineHeight: '24px',
            }}
          >
            {activeSection === 'Overview' && 'You are now in the Organization page where you can find all the data about your team members, workspaces, policies, and more.'}
            {activeSection === 'Team' && 'Manage your organization team members and roles'}
            {activeSection === 'Workspace' && 'Manage your organization workspaces'}
            {activeSection === 'Statistics' && (
              <>
                View dashboards and analytics across your organization
                {statisticsSubSection === 'gateway' && ' — Cloud Storage Gateway'}
                {statisticsSubSection === 'chat' && ' — Private Chat Telemetry'}
                {statisticsSubSection === 'policies' && ' — Policies Analytics'}
                {statisticsSubSection === 'credits' && ' — Credits & Billing'}
              </>
            )}
            {activeSection === 'Billing' && 'Manage your subscription and billing'}
            {activeSection === 'Activation Links' && 'Manage activation links for new users'}
            {activeSection === 'Policies' && 'Configure data and security policies'}
          </Typography>
        </div>
        {/* Billing Management Card removed per design */}
      </div>

      {/* Main Content Card */}
      <div style={{ padding: '0 40px 40px 40px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' }}>
          {/* Content - No tabs, controlled by sidebar */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Home Section (formerly Overview) */}
            {activeSection === 'Overview' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Resource Badges - 4 cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                  {/* Team Members */}
                  <Box sx={{ p: 2, borderRadius: '10px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Team Members</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{team.length}</Typography>
                  </Box>
                  {/* Workspaces */}
                  <Box sx={{ p: 2, borderRadius: '10px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Workspaces</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{globalData.workspaces?.length || 0}</Typography>
                  </Box>
                  {/* Repositories */}
                  <Box sx={{ p: 2, borderRadius: '10px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Repositories</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{repositoriesCount}</Typography>
                  </Box>
                  {/* Resources (total) */}
                  <Box sx={{ p: 2, borderRadius: '10px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><rect width="20" height="8" x="2" y="2" rx="2" ry="2"/><rect width="20" height="8" x="2" y="14" rx="2" ry="2"/><line x1="6" x2="6.01" y1="6" y2="6"/><line x1="6" x2="6.01" y1="18" y2="18"/></svg>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Resources</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{bucketsCount + repositoriesCount}</Typography>
                  </Box>
                </Box>

                {/* General Information - with Credit Overview replacing quick stats */}
                <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #E9EAEB' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 3 }}>
                      General Information
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 4, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      {/* Company fields */}
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '300px' } }}>
                        <Box sx={{ 
                          p: 2.5, 
                          borderRadius: '10px', 
                          border: '1px solid #E5E7EB',
                          backgroundColor: '#FAFAFA',
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {/* Row 1: Name + Domain */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <div style={{ flex: 1 }}>
                                <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#667085' }}>
                                  Organization Name {isEditingCompany && <span style={{ color: '#E11D48' }}>*</span>}
                                </Typography>
                                {isEditingCompany ? (
                                  <TextField
                                    fullWidth
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    placeholder="Enter organization name"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 500 }}>
                                    {companyName || '—'}
                                  </Typography>
                                )}
                              </div>
                              <div style={{ flex: 1 }}>
                                <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#667085' }}>
                                  Domain {isEditingCompany && <span style={{ color: '#E11D48' }}>*</span>}
                                </Typography>
                                {isEditingCompany ? (
                                  <TextField
                                    fullWidth
                                    value={companyDomain}
                                    onChange={(e) => setCompanyDomain(e.target.value)}
                                    placeholder="acme.com"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 500 }}>
                                    {companyDomain || '—'}
                                  </Typography>
                                )}
                              </div>
                            </Box>

                            {/* Row 2: Address (aggregated) + Empty placeholder for half-column */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <div style={{ flex: 1 }}>
                                <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#667085' }}>
                                  Address {isEditingCompany && <span style={{ color: '#E11D48' }}>*</span>}
                                </Typography>
                                {isEditingCompany ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                      fullWidth
                                      value={companyAddress1}
                                      onChange={(e) => setCompanyAddress1(e.target.value)}
                                      placeholder="Address Line 1"
                                      size="small"
                                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                    />
                                    <TextField
                                      fullWidth
                                      value={companyAddress2}
                                      onChange={(e) => setCompanyAddress2(e.target.value)}
                                      placeholder="Address Line 2 (optional)"
                                      size="small"
                                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <TextField
                                        value={companyCity}
                                        onChange={(e) => setCompanyCity(e.target.value)}
                                        placeholder="City"
                                        size="small"
                                        sx={{ flex: 2, '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                      />
                                      <TextField
                                        value={companyZipcode}
                                        onChange={(e) => setCompanyZipcode(e.target.value)}
                                        placeholder="Zip Code"
                                        size="small"
                                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <TextField
                                        value={companyState}
                                        onChange={(e) => setCompanyState(e.target.value)}
                                        placeholder="State / Province"
                                        size="small"
                                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                      />
                                      <TextField
                                        value={companyCountry}
                                        onChange={(e) => setCompanyCountry(e.target.value)}
                                        placeholder="Country"
                                        size="small"
                                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                      />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 500, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                                    {[
                                      companyAddress1,
                                      companyAddress2,
                                      [companyCity, companyState, companyZipcode].filter(Boolean).join(', '),
                                      companyCountry
                                    ].filter(Boolean).join('\n') || '—'}
                                  </Typography>
                                )}
                              </div>
                              <div style={{ flex: 1 }}>
                                {/* Empty placeholder to maintain half-column layout */}
                              </div>
                            </Box>

                            {/* Row 5: Phone + Website */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <div style={{ flex: 1 }}>
                                <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#667085' }}>
                                  Phone
                                </Typography>
                                {isEditingCompany ? (
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Select
                                      value={phoneCountryCode}
                                      onChange={(e) => setPhoneCountryCode(e.target.value)}
                                      size="small"
                                      sx={{
                                        minWidth: '90px',
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        fontSize: '12px',
                                      }}
                                      renderValue={(value) => {
                                        const PHONE_CODES = [
                                          { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}' },
                                          { code: '+44', country: 'GB', flag: '\u{1F1EC}\u{1F1E7}' },
                                          { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}' },
                                          { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}' },
                                          { code: '+34', country: 'ES', flag: '\u{1F1EA}\u{1F1F8}' },
                                          { code: '+39', country: 'IT', flag: '\u{1F1EE}\u{1F1F9}' },
                                          { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}' },
                                          { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}' },
                                          { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}' },
                                          { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}' },
                                          { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}' },
                                          { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}' },
                                          { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}' },
                                        ];
                                        const item = PHONE_CODES.find(c => c.code === value);
                                        return item ? `${item.flag} ${item.code}` : value;
                                      }}
                                    >
                                      {[
                                        { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}' },
                                        { code: '+1', country: 'CA', flag: '\u{1F1E8}\u{1F1E6}' },
                                        { code: '+44', country: 'GB', flag: '\u{1F1EC}\u{1F1E7}' },
                                        { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}' },
                                        { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}' },
                                        { code: '+34', country: 'ES', flag: '\u{1F1EA}\u{1F1F8}' },
                                        { code: '+39', country: 'IT', flag: '\u{1F1EE}\u{1F1F9}' },
                                        { code: '+351', country: 'PT', flag: '\u{1F1F5}\u{1F1F9}' },
                                        { code: '+31', country: 'NL', flag: '\u{1F1F3}\u{1F1F1}' },
                                        { code: '+32', country: 'BE', flag: '\u{1F1E7}\u{1F1EA}' },
                                        { code: '+41', country: 'CH', flag: '\u{1F1E8}\u{1F1ED}' },
                                        { code: '+46', country: 'SE', flag: '\u{1F1F8}\u{1F1EA}' },
                                        { code: '+47', country: 'NO', flag: '\u{1F1F3}\u{1F1F4}' },
                                        { code: '+45', country: 'DK', flag: '\u{1F1E9}\u{1F1F0}' },
                                        { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}' },
                                        { code: '+82', country: 'KR', flag: '\u{1F1F0}\u{1F1F7}' },
                                        { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}' },
                                        { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}' },
                                        { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}' },
                                        { code: '+64', country: 'NZ', flag: '\u{1F1F3}\u{1F1FF}' },
                                        { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}' },
                                        { code: '+52', country: 'MX', flag: '\u{1F1F2}\u{1F1FD}' },
                                        { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}' },
                                        { code: '+972', country: 'IL', flag: '\u{1F1EE}\u{1F1F1}' },
                                        { code: '+90', country: 'TR', flag: '\u{1F1F9}\u{1F1F7}' },
                                        { code: '+27', country: 'ZA', flag: '\u{1F1FF}\u{1F1E6}' },
                                        { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}' },
                                        { code: '+60', country: 'MY', flag: '\u{1F1F2}\u{1F1FE}' },
                                        { code: '+66', country: 'TH', flag: '\u{1F1F9}\u{1F1ED}' },
                                        { code: '+380', country: 'UA', flag: '\u{1F1FA}\u{1F1E6}' },
                                        { code: '+7', country: 'RU', flag: '\u{1F1F7}\u{1F1FA}' },
                                      ].map((c, idx) => (
                                        <MenuItem key={`${c.code}-${c.country}-${idx}`} value={c.code}>
                                          {c.flag} {c.country} ({c.code})
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <TextField
                                      fullWidth
                                      value={companyPhone}
                                      onChange={(e) => setCompanyPhone(e.target.value)}
                                      placeholder="555-0123"
                                      size="small"
                                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                    />
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 500 }}>
                                    {companyPhone ? `${phoneCountryCode}${companyPhone}` : '—'}
                                  </Typography>
                                )}
                              </div>
                              <div style={{ flex: 1 }}>
                                <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#667085' }}>
                                  Website {isEditingCompany && <span style={{ color: '#E11D48' }}>*</span>}
                                </Typography>
                                {isEditingCompany ? (
                                  <TextField
                                    fullWidth
                                    value={companyWebsite}
                                    onChange={(e) => setCompanyWebsite(e.target.value)}
                                    placeholder="https://acme.com"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: '8px' } }}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 500 }}>
                                    {companyWebsite || '—'}
                                  </Typography>
                                )}
                              </div>
                            </Box>

                            {/* Row 6: MFA Enforcement */}
                            <div>
                              <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#667085' }}>
                                Enforce MFA
                              </Typography>
                              {isEditingCompany ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box
                                    onClick={() => setCompanyMfaEnforced(!companyMfaEnforced)}
                                    sx={{
                                      width: 40,
                                      height: 22,
                                      borderRadius: '11px',
                                      bgcolor: companyMfaEnforced ? '#617BFF' : '#D0D5DD',
                                      cursor: 'pointer',
                                      position: 'relative',
                                      transition: 'background-color 0.2s',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        bgcolor: 'white',
                                        position: 'absolute',
                                        top: 2,
                                        left: companyMfaEnforced ? 20 : 2,
                                        transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                      }}
                                    />
                                  </Box>
                                  <Typography sx={{ fontSize: '13px', color: companyMfaEnforced ? '#617BFF' : '#667085', fontWeight: 500 }}>
                                    {companyMfaEnforced ? 'Enabled' : 'Disabled'}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: companyMfaEnforced ? '#10B981' : '#D0D5DD',
                                    }}
                                  />
                                  <Typography sx={{ fontSize: '14px', color: '#101828', fontWeight: 500 }}>
                                    {companyMfaEnforced ? 'Enabled' : 'Disabled'}
                                  </Typography>
                                </Box>
                              )}
                              <Typography sx={{ fontSize: '11px', color: '#98A2B3', mt: 0.5 }}>
                                When enabled, all organization members will be required to configure MFA.
                              </Typography>
                            </div>
                          </Box>

                          {/* Edit/Save button inside the frame */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, pt: 1.5, borderTop: '1px solid #E5E7EB' }}>
                            {isEditingCompany && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setIsEditingCompany(false)}
                                sx={{ 
                                  textTransform: 'none', 
                                  mr: 1.5,
                                  color: '#667085',
                                  borderColor: '#E5E7EB',
                                  '&:hover': { borderColor: '#D0D5DD', backgroundColor: 'white' }
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              size="small"
                              onClick={async () => {
                                if (isEditingCompany) {
                                  await handleSaveCompany();
                                  setIsEditingCompany(false);
                                } else {
                                  setIsEditingCompany(true);
                                }
                              }}
                              disabled={companyLoading || (isEditingCompany && (!companyName.trim() || !companyDomain.trim() || !companyAddress1.trim() || !companyCity.trim() || !companyCountry.trim() || !companyWebsite.trim()))}
                              sx={{ textTransform: 'none', bgcolor: '#617BFF', '&:hover': { bgcolor: '#4A67E3' }, '&:disabled': { bgcolor: '#617BFF', opacity: 0.5 } }}
                            >
                              {companyLoading ? 'Saving...' : (isEditingCompany ? 'Save' : 'Edit')}
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      {/* Credit Overview Card */}
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '300px' } }}>
                        <Box sx={{ 
                          p: 2.5, 
                          borderRadius: '10px', 
                          border: '1px solid #E5E7EB',
                          backgroundColor: '#FAFAFA',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                        }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 2 }}>
                            Credit Overview
                          </Typography>
                          {/* Utilization Credit Usage + bar */}
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>
                              Utilization Credit Usage
                            </Typography>
                            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#101828', mb: 1 }}>
                              {creditsBalance ? creditsBalance.realtimeBalance.toLocaleString() : '0'}{' '}
                              <span style={{ fontSize: '13px', fontWeight: 500, color: '#667085' }}>
                                / {creditsBalance ? creditsBalance.creditBalance.toLocaleString() : '0'} credits
                              </span>
                            </Typography>
                            <Box sx={{ width: '100%', height: 8, bgcolor: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                              <Box 
                                sx={{ 
                                  width: `${creditsBalance && creditsBalance.creditBalance > 0 
                                    ? Math.min(100, ((creditsBalance.creditBalance - creditsBalance.realtimeBalance) / creditsBalance.creditBalance) * 100) 
                                    : 0}%`, 
                                  height: '100%', 
                                  bgcolor: '#617BFF', 
                                  borderRadius: 4 
                                }} 
                              />
                            </Box>
                            <Typography sx={{ fontSize: '11px', color: '#667085', mt: 0.5 }}>
                              {creditsBalance && creditsBalance.creditBalance > 0 
                                ? `${Math.round(((creditsBalance.creditBalance - creditsBalance.realtimeBalance) / creditsBalance.creditBalance) * 100)}% used`
                                : 'No credits allocated'}
                            </Typography>
                          </Box>
                          {/* Plan / Type / Renewal */}
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>Plan</Typography>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#617BFF' }}>
                                {userQuota?.subscription?.name || currentSubscription?.name || quotaData.subscriptionType || 'Free'}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1, borderLeft: '1px solid #E5E7EB', pl: 2 }}>
                              <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>Type</Typography>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                                {currentSubscription?.periodType === 'YEARLY' ? 'Yearly' : 'Monthly'}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1, borderLeft: '1px solid #E5E7EB', pl: 2 }}>
                              <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>Renewal</Typography>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                                {currentSubscription?.dateTo 
                                  ? new Date(currentSubscription.dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : '—'}
                              </Typography>
                            </Box>
                          </Box>
                          {/* Bottom bar + View More button */}
                          <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setActiveSection('Billing')}
                              sx={{ textTransform: 'none', bgcolor: '#617BFF', '&:hover': { bgcolor: '#4A67E3' } }}
                            >
                              View More
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Top 5 Workspaces & Top 5 Repositories */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
                  {/* Top 5 Workspaces */}
                  <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 2 }}>
                      Top 5 Workspaces
                    </Typography>
                    {topWorkspaces.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#667085', fontSize: '14px' }}>
                        No workspaces data available
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                          <svg viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                            <circle cx="18" cy="18" r="15.915" fill="none" stroke="#E5E7EB" strokeWidth="3"/>
                            {(() => {
                              const total = topWorkspaces.reduce((sum, ws) => sum + ws.reposCount, 0);
                              let offset = 0;
                              return topWorkspaces.map((ws, i) => {
                                const pct = total > 0 ? (ws.reposCount / total) * 100 : 0;
                                const el = (
                                  <circle key={i} cx="18" cy="18" r="15.915" fill="none" stroke={ws.color} strokeWidth="3" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={-offset}/>
                                );
                                offset += pct;
                                return el;
                              });
                            })()}
                          </svg>
                          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>{topWorkspaces.reduce((sum, ws) => sum + ws.reposCount, 0)}</Typography>
                            <Typography sx={{ fontSize: '10px', color: '#667085' }}>Repos</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {topWorkspaces.map((ws, i) => {
                            const total = topWorkspaces.reduce((sum, w) => sum + w.reposCount, 0);
                            const pct = total > 0 ? Math.round((ws.reposCount / total) * 100) : 0;
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ws.color }} />
                                  <Typography sx={{ fontSize: '12px', color: '#344054' }}>{ws.name}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>{ws.reposCount} ({pct}%)</Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Top 5 Repositories */}
                  <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 2 }}>
                      Top 5 Repositories
                    </Typography>
                    {topRepositories.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#667085', fontSize: '14px' }}>
                        No repositories data available
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                          <svg viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                            <circle cx="18" cy="18" r="15.915" fill="none" stroke="#E5E7EB" strokeWidth="3"/>
                            {(() => {
                              const total = topRepositories.reduce((sum, r) => sum + r.bucketsCount + r.aiCount, 0);
                              let offset = 0;
                              return topRepositories.map((repo, i) => {
                                const pct = total > 0 ? ((repo.bucketsCount + repo.aiCount) / total) * 100 : 0;
                                const el = (
                                  <circle key={i} cx="18" cy="18" r="15.915" fill="none" stroke={repo.color} strokeWidth="3" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={-offset}/>
                                );
                                offset += pct;
                                return el;
                              });
                            })()}
                          </svg>
                          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>{topRepositories.reduce((sum, r) => sum + r.bucketsCount + r.aiCount, 0)}</Typography>
                            <Typography sx={{ fontSize: '10px', color: '#667085' }}>Resources</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {topRepositories.map((repo, i) => {
                            const total = topRepositories.reduce((sum, r) => sum + r.bucketsCount + r.aiCount, 0);
                            const repoTotal = repo.bucketsCount + repo.aiCount;
                            const pct = total > 0 ? Math.round((repoTotal / total) * 100) : 0;
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: repo.color }} />
                                  <Typography sx={{ fontSize: '12px', color: '#344054' }}>{repo.name}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>{repoTotal} ({pct}%)</Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {activeSection === 'Team' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Team Overview Sub-section */}
                {teamSubSection === 'overview' && (
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                      Overview
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#667085', mb: 3 }}>
                      Quick summary of your organization's team and usage.
                    </Typography>

                    {/* Team Quota & Stats - Top Section */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
                      {/* Total Members with Quota */}
                      <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '14px', color: '#667085', fontWeight: 500 }}>Total Members</Typography>
                            <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#101828', lineHeight: 1 }}>{team.length}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: '13px', color: '#667085' }}>Team Quota</Typography>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828' }}>
                            {team.length} / {(() => { const q = getQuotaByCode('users.max') || getQuotaByCode('team.max'); return q?.usage.max || 'Unlimited'; })()}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(() => { const q = getQuotaByCode('users.max') || getQuotaByCode('team.max'); if (q && q.usage.max > 0) return Math.min(100, (team.length / q.usage.max) * 100); return 0; })()}
                          sx={{ height: 8, borderRadius: 4, bgcolor: '#E5E7EB', '& .MuiLinearProgress-bar': { bgcolor: '#617BFF', borderRadius: 4 } }}
                        />
                      </Box>

                      {/* Quick Stats Cards */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                          <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Owners</Typography>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{team.filter(m => m.orgRole === 0xff).length}</Typography>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                          <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Administrators</Typography>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{team.filter(m => m.orgRole === 0xfe).length}</Typography>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                          <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Active Members</Typography>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#10B981' }}>{team.filter(m => m.validated).length}</Typography>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                          <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Pending Invites</Typography>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#F59E0B' }}>{team.filter(m => !m.validated).length}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Recent Members (Left) + Role Distribution Pie Chart (Right) */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 4 }}>
                      {/* Recent Members - Left */}
                      <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>Recent Members</Typography>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setTeamSubSection('members')}
                            sx={{ textTransform: 'none', color: '#617BFF', fontWeight: 600, fontSize: '14px' }}
                          >
                            View all →
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {team.slice(0, 4).map((member, idx) => (
                            <Box 
                              key={member.id || idx} 
                              onClick={() => {
                                setSelectedMember(member);
                                setTeamSubSection('member-details');
                              }}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2, 
                                p: 2, 
                                borderRadius: '8px', 
                                border: '1px solid #E5E7EB',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#617BFF',
                                  bgcolor: '#F8F9FF',
                                },
                              }}
                            >
                              <Avatar src={(member as any).imageUrl || ''} alt={member.name || member.email} sx={{ width: 36, height: 36 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name || member.email}</Typography>
                                <Typography sx={{ fontSize: '12px', color: '#667085', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email}</Typography>
                              </Box>
                              <Chip label={getRoleName(member.orgRole ?? 0)} size="small" sx={{ bgcolor: '#F3F4F6', color: '#374151', fontSize: '11px', fontWeight: 500 }} />
                            </Box>
                          ))}
                          {team.length === 0 && (
                            <Typography sx={{ color: '#667085', fontSize: '14px', textAlign: 'center', py: 4 }}>No team members yet.</Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Role Distribution - Right (Pie Chart) */}
                      <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 2 }}>Role Distribution</Typography>
                        {(() => {
                          const owners = team.filter(m => m.orgRole === 0xff).length;
                          const admins = team.filter(m => m.orgRole === 0xfe).length;
                          const members = team.filter(m => m.orgRole !== 0xff && m.orgRole !== 0xfe).length;
                          const total = team.length || 1;
                          const ownerPct = Math.round((owners / total) * 100);
                          const adminPct = Math.round((admins / total) * 100);
                          const memberPct = Math.round((members / total) * 100);
                          
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flex: 1 }}>
                              {/* Donut Chart - Larger */}
                              <Box sx={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
                                <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#E5E7EB" strokeWidth="3"/>
                                  {(() => {
                                    let offset = 0;
                                    const circumference = 100;
                                    return (
                                      <>
                                        {owners > 0 && (
                                          <circle cx="18" cy="18" r="15.915" fill="none" stroke="#617BFF" strokeWidth="3" 
                                            strokeDasharray={`${ownerPct} ${circumference - ownerPct}`} strokeDashoffset={-offset} />
                                        )}
                                        {(() => { offset += ownerPct; return null; })()}
                                        {admins > 0 && (
                                          <circle cx="18" cy="18" r="15.915" fill="none" stroke="#10B981" strokeWidth="3" 
                                            strokeDasharray={`${adminPct} ${circumference - adminPct}`} strokeDashoffset={-offset} />
                                        )}
                                        {(() => { offset += adminPct; return null; })()}
                                        {members > 0 && (
                                          <circle cx="18" cy="18" r="15.915" fill="none" stroke="#F59E0B" strokeWidth="3" 
                                            strokeDasharray={`${memberPct} ${circumference - memberPct}`} strokeDashoffset={-offset} />
                                        )}
                                      </>
                                    );
                                  })()}
                                </svg>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                  <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>{team.length}</Typography>
                                  <Typography sx={{ fontSize: '12px', color: '#667085' }}>Total</Typography>
                                </Box>
                              </Box>
                              {/* Legend with percentages - Full width */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {[
                                  { label: 'Owners', count: owners, pct: ownerPct, color: '#617BFF' },
                                  { label: 'Admins', count: admins, pct: adminPct, color: '#10B981' },
                                  { label: 'Members', count: members, pct: memberPct, color: '#F59E0B' },
                                ].map((item, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: i < 2 ? '1px solid #F3F4F6' : 'none' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                                      <Typography sx={{ fontSize: '14px', color: '#344054', fontWeight: 500 }}>{item.label}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Typography sx={{ fontSize: '16px', color: '#101828', fontWeight: 700 }}>{item.count}</Typography>
                                      <Typography sx={{ fontSize: '13px', color: '#667085', minWidth: 45 }}>({item.pct}%)</Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>

                    {/* Recent Member Actions */}
                    <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', mb: 4 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#101828', mb: 2 }}>Recent Member Actions</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {team.slice(0, 3).map((member, idx) => (
                          <Box key={member.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '8px', bgcolor: '#F9FAFB' }}>
                            <Avatar src={(member as any).imageUrl || ''} alt={member.name || member.email} sx={{ width: 32, height: 32 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '13px', color: '#101828' }}>
                                <strong>{member.name || member.email?.split('@')[0]}</strong> {member.validated ? 'joined the organization' : 'was invited to the organization'}
                              </Typography>
                              <Typography sx={{ fontSize: '11px', color: '#667085' }}>Recently</Typography>
                            </Box>
                          </Box>
                        ))}
                        {team.length === 0 && (
                          <Typography sx={{ color: '#667085', fontSize: '14px', textAlign: 'center', py: 2 }}>No recent actions.</Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Tutorial - Managing Team Members */}
                    <Box sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', bgcolor: '#F0F2FF' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#617BFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                          </svg>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#101828', mb: 1 }}>Managing Team Members</Typography>
                          <Typography sx={{ fontSize: '14px', color: '#667085', mb: 2 }}>
                            Learn how to effectively manage your team by inviting new members, assigning roles, and controlling access permissions.
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip label="Invite members via email" size="small" sx={{ bgcolor: 'white', border: '1px solid #E5E7EB', fontSize: '12px' }} />
                            <Chip label="Assign Owner, Admin, or Member roles" size="small" sx={{ bgcolor: 'white', border: '1px solid #E5E7EB', fontSize: '12px' }} />
                            <Chip label="Manage workspace access" size="small" sx={{ bgcolor: 'white', border: '1px solid #E5E7EB', fontSize: '12px' }} />
                          </Box>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setTeamSubSection('members')}
                            sx={{ textTransform: 'none', color: '#617BFF', fontWeight: 600, fontSize: '14px', mt: 2, p: 0, '&:hover': { bgcolor: 'transparent' } }}
                          >
                            Go to Team Members →
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Team Members Sub-section */}
                {teamSubSection === 'members' && (
                  <Box>
                    {/* Header with title and Add button */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                          Members
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                          Manage your organization's team members, roles, and permissions.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Search */}
                        {showTeamSearch ? (
                          <TextField
                            size="small"
                            placeholder="Search members..."
                            value={teamSearchQuery}
                            onChange={(e) => setTeamSearchQuery(e.target.value)}
                            onBlur={() => { if (!teamSearchQuery) setShowTeamSearch(false); }}
                            autoFocus
                            sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } }}
                            InputProps={{
                              startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                            }}
                          />
                        ) : (
                          <Tooltip title="Search">
                            <IconButton
                              size="small"
                              onClick={() => setShowTeamSearch(true)}
                              sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                            >
                              <SearchIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Reload */}
                        <Tooltip title="Reload">
                          <IconButton
                            size="small"
                            onClick={() => {
                              (async () => {
                                setTeamLoading(true);
                                try {
                                  const users = await loadUsersWithRoles();
                                  setTeam(users);
                                } finally {
                                  setTeamLoading(false);
                                }
                              })();
                            }}
                            disabled={teamLoading}
                            sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                          >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {/* Add */}
                        {canManageUsers && (
                          <Tooltip title="Add member">
                            <IconButton
                              size="small"
                              onClick={() => setAddMemberOpen(!addMemberOpen)}
                              sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
                            >
                              <AddIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {teamLoading && <LinearProgress sx={{ mb: 2 }} />}
                    {teamError && (
                      <Typography sx={{ color: '#F04438', fontSize: '14px', mb: 2 }}>
                        {teamError}
                      </Typography>
                    )}

                    {/* Collapsible Add Member Form */}
                    {canManageUsers && (
                      <Collapse in={addMemberOpen}>
                        <Box sx={{ mb: 3, p: 3, borderRadius: '12px', border: '1px solid #E4E7EC', bgcolor: '#F9FAFB' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 2 }}>
                            Invite a new team member
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField
                              size="small"
                              label="Email"
                              placeholder="user@email.com"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              onBlur={() => { if (inviteEmailError) { try { toast.error(inviteEmailError); } catch (e) { /* ignore */ } } }}
                              error={!!inviteEmailError}
                              helperText={inviteEmailError || ' '}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', '& fieldset': { borderColor: '#D0D5DD' }, '&:hover fieldset': { borderColor: '#98A2B3' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } }, '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 } }}
                            />
                            <MultiRoleSelect value={inviteRole} onChange={setInviteRole} />
                            <TextField
                              size="small"
                              label="First name"
                              placeholder="John"
                              value={inviteFirstName}
                              onChange={(e) => setInviteFirstName(e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', '& fieldset': { borderColor: '#D0D5DD' }, '&:hover fieldset': { borderColor: '#98A2B3' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } }, '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 } }}
                            />
                            <TextField
                              size="small"
                              label="Last name"
                              placeholder="Doe"
                              value={inviteLastName}
                              onChange={(e) => setInviteLastName(e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', '& fieldset': { borderColor: '#D0D5DD' }, '&:hover fieldset': { borderColor: '#98A2B3' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } }, '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 } }}
                            />
                            <FormControl size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', '& fieldset': { borderColor: '#D0D5DD' }, '&:hover fieldset': { borderColor: '#98A2B3' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } }, '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 } }}>
                              <InputLabel>Authentication Method</InputLabel>
                              <Select value={inviteAuthMethod} label="Authentication Method" onChange={(e) => setInviteAuthMethod(e.target.value as 'LOCAL' | 'GOOGLE' | 'GITHUB')}>
                                <MenuItem value="LOCAL">Local</MenuItem>
                                <MenuItem value="GOOGLE">Google</MenuItem>
                                <MenuItem value="GITHUB">GitHub</MenuItem>
                              </Select>
                            </FormControl>
                            {inviteAuthMethod === 'LOCAL' && (
                              <TextField
                                size="small"
                                label="(Optional) Password"
                                type="password"
                                placeholder="(Optional) Enter password"
                                value={invitePassword}
                                onChange={(e) => setInvitePassword(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', '& fieldset': { borderColor: '#D0D5DD' }, '&:hover fieldset': { borderColor: '#98A2B3' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } }, '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 } }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button variant="outlined" size="small" onClick={() => setAddMemberOpen(false)} sx={{ textTransform: 'none', borderColor: '#D0D5DD', color: '#344054', borderRadius: '8px', height: '36px', px: 2 }}>
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleInvite}
                              disabled={!inviteEmail || !!inviteEmailError || !inviteFirstName || !inviteLastName}
                              sx={{ bgcolor: '#617BFF', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: '36px', px: 2, '&:hover': { bgcolor: '#4A67E3' }, '&.Mui-disabled': { bgcolor: '#F2F4F7', color: '#98A2B3' } }}
                            >
                              Send Invite
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    )}

                    {/* Team Members Table */}
                    <div className="rounded-lg border border-[#E9EAEB]">
                      <div className="overflow-x-auto">
                        <table className="w-full table-auto">
                          <thead>
                            <tr className="border-b border-gray-200" style={{ backgroundColor: '#F9FAFB', height: '44px' }}>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {teamLoading ? (
                              <tr>
                                <td colSpan={5} className="px-4 py-8 text-gray-500 text-center">Loading team members...</td>
                              </tr>
                            ) : team.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-4 py-8 text-gray-500 text-center">No team members yet. Click + to invite someone.</td>
                              </tr>
                            ) : (
                              team.filter(m => {
                                if (!teamSearchQuery) return true;
                                const q = teamSearchQuery.toLowerCase();
                                return (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
                              }).map((member, idx) => {
                                const memberRole = member.orgRole ?? 0;
                                return (
                                  <tr 
                                      key={member.id || idx} 
                                      className="hover:bg-gray-50 transition-all cursor-pointer" 
                                      style={{ height: '64px' }}
                                      onClick={() => { setSelectedMember(member); setTeamSubSection('member-details'); }}
                                    >
                                    <td className="px-4 py-3">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar src={(member as any).imageUrl || ''} alt={member.name || member.email} sx={{ width: 32, height: 32 }} />
                                        <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{member.name || '-'}</Typography>
                                      </Box>
                                    </td>
                                    <td className="px-4 py-3">
                                      <Typography sx={{ fontSize: '14px', color: '#667085' }}>{member.email}</Typography>
                                    </td>
                                    <td className="px-4 py-3">
                                      <Chip label={getRoleName(memberRole)} size="small" sx={{ bgcolor: memberRole === 0xff ? '#F0F2FF' : memberRole === 0xfe ? '#FEF3C7' : '#F3F4F6', color: memberRole === 0xff ? '#617BFF' : memberRole === 0xfe ? '#B45309' : '#374151', fontSize: '12px', fontWeight: 500 }} />
                                    </td>
                                    <td className="px-4 py-3">
                                      <Chip label={member.validated ? 'Active' : 'Pending'} size="small" sx={{ bgcolor: member.validated ? '#D1FAE5' : '#FEF3C7', color: member.validated ? '#065F46' : '#B45309', fontSize: '12px', fontWeight: 500 }} />
                                    </td>
                                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        {canManageUsers ? (
                                          <>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => { e.stopPropagation(); setMemberMenuAnchor(e.currentTarget); setMemberMenuId(member.id); }}
                                              sx={{ color: '#667085', '&:hover': { bgcolor: '#F5F7FF' } }}
                                            >
                                              <MoreVertIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                            <Menu
                                              anchorEl={memberMenuAnchor}
                                              open={memberMenuId === member.id && Boolean(memberMenuAnchor)}
                                              onClose={() => { setMemberMenuAnchor(null); setMemberMenuId(null); }}
                                              PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 160 } }}
                                            >
                                              <MenuItem onClick={() => { setSelectedMember(member); setTeamSubSection('member-details'); setMemberMenuAnchor(null); setMemberMenuId(null); }} sx={{ fontSize: '14px', gap: 1 }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> View
                                              </MenuItem>
                                              <MenuItem onClick={() => { setEditRoleMember(member); setEditRoleValue(memberRole); setEditRoleModalOpen(true); setMemberMenuAnchor(null); setMemberMenuId(null); }} sx={{ fontSize: '14px', gap: 1 }}>
                                                <EditIcon sx={{ fontSize: 16 }} /> Edit role
                                              </MenuItem>
                                              {!member.validated && (
                                                <MenuItem onClick={() => { handleResendInvitation(member.id); setMemberMenuAnchor(null); setMemberMenuId(null); }} sx={{ fontSize: '14px', gap: 1 }}>
                                                  <EmailIcon sx={{ fontSize: 16 }} /> Resend invitation
                                                </MenuItem>
                                              )}
                                              <MenuItem
                                                onClick={() => { if ((authState.user?.email || '') !== member.email && window.confirm('Remove this user from the organization?')) { handleDelete(member.id); } setMemberMenuAnchor(null); setMemberMenuId(null); }}
                                                disabled={(authState.user?.email || '') === member.email}
                                                sx={{ fontSize: '14px', gap: 1, color: (authState.user?.email || '') === member.email ? '#D0D5DD' : '#F04438' }}
                                              >
                                                <DeleteOutlineIcon sx={{ fontSize: 16 }} /> Delete
                                              </MenuItem>
                                            </Menu>
                                          </>
                                        ) : null}
                                      </Box>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Box>
                )}

                {/* Member Details Sub-section (Full Page View) */}
                {teamSubSection === 'member-details' && selectedMember && (
                  <Box>
                    {/* Back button */}
                    <Button
                      variant="text"
                      startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
                      onClick={() => { setTeamSubSection('members'); setSelectedMember(null); }}
                      sx={{ mb: 2, color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
                    >
                      Back to Members
                    </Button>

                    {/* Member Profile Header */}
                    <Card sx={{ mb: 3, borderRadius: '12px', border: '1px solid #E9EAEB', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Avatar src={(selectedMember as any).imageUrl || ''} alt={selectedMember.name || selectedMember.email} sx={{ width: 80, height: 80 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '24px', color: '#101828', mb: 0.5 }}>{selectedMember.name || '-'}</Typography>
                            <Typography sx={{ fontSize: '14px', color: '#667085', mb: 1 }}>{selectedMember.email}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                              <Chip label={getRoleName(selectedMember.orgRole ?? 0)} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '12px', fontWeight: 500 }} />
                              <Chip label={selectedMember.validated ? 'Active' : 'Pending'} size="small" sx={{ bgcolor: selectedMember.validated ? '#D1FAE5' : '#FEF3C7', color: selectedMember.validated ? '#065F46' : '#B45309', fontSize: '12px', fontWeight: 500 }} />
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#667085', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                              Last activity: {(selectedMember as any).lastActivityAt ? new Date((selectedMember as any).lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                            </Typography>
                          </Box>
                          {canManageUsers && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => { setEditRoleMember(selectedMember); setEditRoleValue(selectedMember.orgRole ?? 0); setEditRoleModalOpen(true); }}
                              sx={{ textTransform: 'none', borderColor: '#D0D5DD', color: '#344054', borderRadius: '8px', '&:hover': { borderColor: '#617BFF', bgcolor: '#F9FAFB' } }}
                            >
                              Edit
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Activity Statistics - Last 30 days (real per-user data from API) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>Activity Statistics</Typography>
                      {memberStatsLoading && <CircularProgress size={16} sx={{ color: '#617BFF' }} />}
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#667085', mb: 2 }}>Last 30 days • {selectedMember.name || selectedMember.email?.split('@')[0]}</Typography>
                    {(() => {
                      const conversations = memberStats?.conversations ?? 0;
                      const totalTokens = memberStats?.totalTokens ?? 0;
                      const messagesSent = memberStats?.messagesSent ?? 0;
                      
                      const formatTokens = (tokens: number): string => {
                        if (tokens >= 1000000) return (tokens / 1000000).toFixed(1) + 'M';
                        if (tokens >= 1000) return (tokens / 1000).toFixed(1) + 'K';
                        return tokens.toString();
                      };
                      
                      return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
                          {/* Conversations (Private Chat) */}
                          <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                </Box>
                                <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Conversations</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>{conversations}</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#667085' }}>Private chat sessions</Typography>
                            </CardContent>
                          </Card>

                          {/* Total Tokens (in + out) */}
                          <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>
                                </Box>
                                <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Total Tokens</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>{formatTokens(totalTokens)}</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#667085' }}>Input + Output tokens</Typography>
                            </CardContent>
                          </Card>

                          {/* Messages Sent */}
                          <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                </Box>
                                <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Messages Sent</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>{messagesSent}</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#667085' }}>Total messages</Typography>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })()}

                    {/* Workspace Access */}
                    <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>Workspace Access</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#667085', mb: 2 }}>Permissions and resources per workspace</Typography>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #E9EAEB', boxShadow: 'none', mb: 4 }}>
                      <div className="rounded-lg overflow-hidden">
                        <table className="w-full table-auto">
                          <thead>
                            <tr className="border-b border-gray-200" style={{ backgroundColor: '#F9FAFB', height: '44px' }}>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workspace</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resources</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Repositories</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {globalData.workspaces?.length > 0 ? (
                              globalData.workspaces.map((ws: any) => (
                                <tr key={ws.id} className="hover:bg-gray-50 transition-all" style={{ height: '56px' }}>
                                  <td className="px-4 py-3">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Box sx={{ width: 32, height: 32, borderRadius: '6px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                          <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                          <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                                        </svg>
                                      </Box>
                                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#101828' }}>{ws.name}</Typography>
                                    </Box>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Chip label={getRoleName(selectedMember.orgRole ?? 0)} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '11px', fontWeight: 500 }} />
                                  </td>
                                  <td className="px-4 py-3">
                                    <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                                      {(workspaceResourceCounts[ws.id]?.buckets || 0) + (workspaceResourceCounts[ws.id]?.aiLlms || 0)}
                                    </Typography>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                                      {workspaceResourceCounts[ws.id]?.repos || 0}
                                    </Typography>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} className="px-4 py-8 text-gray-500 text-center">No workspace access</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    {/* Activity Logs */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>Activity Logs</Typography>
                      {memberEventsLoading && <CircularProgress size={16} sx={{ color: '#617BFF' }} />}
                    </Box>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #E9EAEB', boxShadow: 'none', mb: 4 }}>
                      {memberEventsLoading ? (
                        <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ fontSize: '14px', color: '#667085' }}>Loading activity...</Typography>
                        </CardContent>
                      ) : memberEvents.length === 0 ? (
                        <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2">
                              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                          </Box>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#101828', mb: 0.5 }}>
                            No activity logs found
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#667085', textAlign: 'center' }}>
                            No recent activity recorded for this user.
                          </Typography>
                        </CardContent>
                      ) : (
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {memberEvents.slice(0, 50).map((ev: any, idx: number) => {
                            // Workspace name
                            const wsName = ev.workspaceId ? (() => {
                              const list = globalData?.workspaces || [];
                              const ws = Array.isArray(list) ? list.find((w: any) => w?.id === ev.workspaceId) : undefined;
                              return ws?.name || null;
                            })() : null;

                            return (
                              <Box
                                key={ev.id || idx}
                                sx={{
                                  px: 2, py: 1.5,
                                  borderBottom: idx < Math.min(memberEvents.length, 50) - 1 ? '1px solid #F2F4F7' : 'none',
                                  display: 'flex', alignItems: 'center', gap: 1.5,
                                  backgroundColor: ev.showUnread ? 'rgba(97,123,255,0.04)' : 'transparent',
                                  borderLeft: ev.showUnread ? '3px solid #617BFF' : '3px solid transparent',
                                  '&:hover': { bgcolor: '#F9FAFB' },
                                }}
                              >
                                {/* Event Icon */}
                                <img 
                                  src={getEventIcon(ev.event)} 
                                  alt="" 
                                  style={{ width: 36, height: 36, flexShrink: 0 }} 
                                />
                                {/* Event Content */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {getEventMessage(ev.context, ev.event, ev.jsonData)}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                    {wsName && (
                                      <Typography sx={{ fontSize: '11px', color: '#667085' }}>
                                        {wsName}
                                      </Typography>
                                    )}
                                    {wsName && (
                                      <Typography sx={{ fontSize: '11px', color: '#D0D5DD' }}>•</Typography>
                                    )}
                                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                                      {new Date(ev.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Card>

                  </Box>
                )}
              </Box>
            )}

            {activeSection === 'Workspace' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Workspace Overview Sub-section */}
                {workspaceSubSection === 'overview' && (
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                      Overview
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#667085', mb: 3 }}>
                      Quick summary of your organization's workspaces and resources.
                    </Typography>

                    {/* Workspace Stats Cards */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
                      {/* Total Workspaces Card */}
                      <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ 
                              width: 36, height: 36, borderRadius: '8px', bgcolor: '#F0F2FF', 
                              display: 'flex', alignItems: 'center', justifyContent: 'center' 
                            }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                              </svg>
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Total Workspaces</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                            {globalData.workspaces?.length || 0}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#667085' }}>Active workspaces</Typography>
                        </CardContent>
                      </Card>

                      {/* Total Buckets Card */}
                      <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ 
                              width: 36, height: 36, borderRadius: '8px', bgcolor: '#F0FDF4', 
                              display: 'flex', alignItems: 'center', justifyContent: 'center' 
                            }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                              </svg>
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Total Buckets</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                            {bucketsCount}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#667085' }}>Across all workspaces</Typography>
                        </CardContent>
                      </Card>

                      {/* Total Repositories Card */}
                      <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ 
                              width: 36, height: 36, borderRadius: '8px', bgcolor: '#FEF3C7', 
                              display: 'flex', alignItems: 'center', justifyContent: 'center' 
                            }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                              </svg>
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Total Repositories</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                            {repositoriesCount}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#667085' }}>Across all workspaces</Typography>
                        </CardContent>
                      </Card>

                      {/* Total AI LLMs Card */}
                      <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ 
                              width: 36, height: 36, borderRadius: '8px', bgcolor: '#EDE9FE', 
                              display: 'flex', alignItems: 'center', justifyContent: 'center' 
                            }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2">
                                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                                <circle cx="7.5" cy="14.5" r="1.5"/><circle cx="16.5" cy="14.5" r="1.5"/>
                              </svg>
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#667085', fontWeight: 500 }}>Total AI LLMs</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                            {aiLlmsCount}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#667085' }}>Connected AI models</Typography>
                        </CardContent>
                      </Card>
                    </Box>

                    {/* Workspaces List with Pagination */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
                          Workspaces
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                        {globalData.workspaces?.slice(workspacePage * workspacesPerPage, (workspacePage + 1) * workspacesPerPage).map((ws: any) => (
                          <Box
                            key={ws.id}
                            onClick={() => selectWorkspaceWithDetails(ws)}
                            sx={{
                              p: 2,
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              bgcolor: 'transparent',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: '#F9FAFB', borderColor: '#617BFF' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                width: 32, height: 32, borderRadius: '6px', bgcolor: '#F0F2FF',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                  <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                                </svg>
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                                  {ws.name}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      {/* Pagination */}
                      {globalData.workspaces && globalData.workspaces.length > workspacesPerPage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={workspacePage === 0}
                            onClick={() => setWorkspacePage(p => Math.max(0, p - 1))}
                            sx={{ 
                              textTransform: 'none', 
                              borderColor: '#D0D5DD', 
                              color: '#344054', 
                              borderRadius: '8px',
                              minWidth: 80,
                              '&:hover': { borderColor: '#617BFF', bgcolor: '#F9FAFB' },
                              '&.Mui-disabled': { borderColor: '#E9EAEB', color: '#D0D5DD' }
                            }}
                          >
                            Previous
                          </Button>
                          <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                            Page {workspacePage + 1} of {Math.ceil(globalData.workspaces.length / workspacesPerPage)}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={(workspacePage + 1) * workspacesPerPage >= globalData.workspaces.length}
                            onClick={() => setWorkspacePage(p => p + 1)}
                            sx={{ 
                              textTransform: 'none', 
                              borderColor: '#D0D5DD', 
                              color: '#344054', 
                              borderRadius: '8px',
                              minWidth: 80,
                              '&:hover': { borderColor: '#617BFF', bgcolor: '#F9FAFB' },
                              '&.Mui-disabled': { borderColor: '#E9EAEB', color: '#D0D5DD' }
                            }}
                          >
                            Next
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Workspace Management Sub-section */}
                {workspaceSubSection === 'management' && (
                  <Box>
                    {/* Header with title and action buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                          Management
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                          Manage your organization's workspaces and their members.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Search */}
                        {showWorkspaceSearch ? (
                          <TextField
                            size="small"
                            placeholder="Search workspaces..."
                            value={workspaceSearchQuery}
                            onChange={(e) => setWorkspaceSearchQuery(e.target.value)}
                            onBlur={() => { if (!workspaceSearchQuery) setShowWorkspaceSearch(false); }}
                            autoFocus
                            sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } }}
                            InputProps={{
                              startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                            }}
                          />
                        ) : (
                          <Tooltip title="Search">
                            <IconButton
                              size="small"
                              onClick={() => setShowWorkspaceSearch(true)}
                              sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                            >
                              <SearchIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Reload */}
                        <Tooltip title="Reload">
                          <IconButton
                            size="small"
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('workspaceChanged'));
                              fetchEnrichedWorkspaces();
                            }}
                            sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                          >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {/* Add */}
                        <Tooltip title="Create workspace">
                          <IconButton
                            size="small"
                            onClick={() => setAddWorkspaceOpen(!addWorkspaceOpen)}
                            sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
                          >
                            <AddIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Collapsible Add Workspace Form */}
                    <Collapse in={addWorkspaceOpen}>
                      <Box sx={{ mb: 3, p: 3, borderRadius: '12px', border: '1px solid #E4E7EC', bgcolor: '#F9FAFB' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 2 }}>
                          Create a new workspace
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                          <TextField
                            size="small"
                            label="Workspace name"
                            placeholder="My Workspace"
                            value={newWorkspaceName}
                            onChange={(e) => setNewWorkspaceName(e.target.value)}
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', '& fieldset': { borderColor: '#D0D5DD' }, '&:hover fieldset': { borderColor: '#98A2B3' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } }, '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 } }}
                          />
                          <Button variant="outlined" size="small" onClick={() => { setAddWorkspaceOpen(false); setNewWorkspaceName(''); }} sx={{ textTransform: 'none', borderColor: '#D0D5DD', color: '#344054', borderRadius: '8px', height: '40px', px: 2 }}>
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateWorkspace}
                            disabled={!newWorkspaceName.trim() || isCreatingWorkspace}
                            sx={{ bgcolor: '#617BFF', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: '40px', px: 2, '&:hover': { bgcolor: '#4A67E3' }, '&.Mui-disabled': { bgcolor: '#F2F4F7', color: '#98A2B3' } }}
                          >
                            {isCreatingWorkspace ? 'Creating...' : 'Create'}
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>

                    {/* Workspaces Table */}
                    <div className="rounded-lg border border-[#E9EAEB]">
                      <div className="overflow-x-auto">
                        <table className="w-full table-auto">
                          <thead>
                            <tr className="border-b border-gray-200" style={{ backgroundColor: '#F9FAFB', height: '44px' }}>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workspace</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {loadingWorkspaces ? (
                              <tr>
                                <td colSpan={5} className="px-4 py-8 text-center">
                                  <CircularProgress size={24} sx={{ color: '#617BFF' }} />
                                </td>
                              </tr>
                            ) : !enrichedWorkspaces || enrichedWorkspaces.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-4 py-8 text-gray-500 text-center">No workspaces yet. Click + to create one.</td>
                              </tr>
                            ) : (
                              enrichedWorkspaces.filter((ws: any) => {
                                if (!workspaceSearchQuery) return true;
                                const q = workspaceSearchQuery.toLowerCase();
                                return (ws.name || '').toLowerCase().includes(q) || (ws.id || '').toLowerCase().includes(q);
                              }).map((ws: any, idx: number) => {
                                return (
                                  <tr 
                                      key={ws.id || idx} 
                                      className="hover:bg-gray-50 transition-all cursor-pointer" 
                                      style={{ height: '64px' }}
                                      onClick={() => selectWorkspaceWithDetails(ws)}
                                    >
                                    <td className="px-4 py-3">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ 
                                          width: 32, height: 32, borderRadius: '8px', 
                                          bgcolor: '#F0F2FF',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                            <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                                          </svg>
                                        </Box>
                                        <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#101828' }}>{ws.name}</Typography>
                                      </Box>
                                    </td>
                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                      <Box
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await navigator.clipboard.writeText(ws.id);
                                            toast.success('Workspace ID copied!');
                                          } catch (e) {
                                            const ta = document.createElement('textarea');
                                            ta.value = ws.id;
                                            document.body.appendChild(ta);
                                            ta.select();
                                            document.execCommand('copy');
                                            document.body.removeChild(ta);
                                            toast.success('Workspace ID copied!');
                                          }
                                        }}
                                        sx={{ 
                                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                          px: 1, py: 0.5, borderRadius: '4px', cursor: 'pointer',
                                          bgcolor: '#F9FAFB', border: '1px solid #E5E7EB',
                                          '&:hover': { bgcolor: '#F3F4F6' }
                                        }}
                                      >
                                        <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#667085' }}>
                                          {ws.id.substring(0, 8)}...
                                        </Typography>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                                          <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                        </svg>
                                      </Box>
                                    </td>
                                    <td className="px-4 py-3">
                                      <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                                        {ws.users?.length || 0} member{(ws.users?.length || 0) !== 1 ? 's' : ''}
                                      </Typography>
                                    </td>
                                    <td className="px-4 py-3">
                                      <Chip 
                                        label="Active" 
                                        size="small" 
                                        sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontSize: '12px', fontWeight: 500 }} 
                                      />
                                    </td>
                                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); setWorkspaceMenuAnchor(e.currentTarget); setWorkspaceMenuId(ws.id); }}
                                          sx={{ color: '#667085', '&:hover': { bgcolor: '#F5F7FF' } }}
                                        >
                                          <MoreVertIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                        <Menu
                                          anchorEl={workspaceMenuAnchor}
                                          open={workspaceMenuId === ws.id && Boolean(workspaceMenuAnchor)}
                                          onClose={() => { setWorkspaceMenuAnchor(null); setWorkspaceMenuId(null); }}
                                          PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 160 } }}
                                        >
                                          <MenuItem onClick={() => { selectWorkspaceWithDetails(ws); setWorkspaceMenuAnchor(null); setWorkspaceMenuId(null); }} sx={{ fontSize: '14px', gap: 1 }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> View
                                          </MenuItem>
                                          <MenuItem onClick={() => { openWorkspaceDetails(ws); setWorkspaceMenuAnchor(null); setWorkspaceMenuId(null); }} sx={{ fontSize: '14px', gap: 1 }}>
                                            <EditIcon sx={{ fontSize: 16 }} /> Edit workspace
                                          </MenuItem>
                                          <MenuItem 
                                            onClick={() => { 
                                              navigate(`/ai/policies?workspaceId=${ws.id}`);
                                              setWorkspaceMenuAnchor(null); 
                                              setWorkspaceMenuId(null); 
                                            }} 
                                            sx={{ fontSize: '14px', gap: 1 }}
                                          >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> Manage Policies
                                          </MenuItem>
                                          <MenuItem
                                            onClick={() => { 
                                              if (enrichedWorkspaces.length === 1) {
                                                toast.error('Cannot delete the only workspace');
                                              } else if (window.confirm(`Delete workspace "${ws.name}"? This action cannot be undone.`)) {
                                                toast.info('Workspace deletion coming soon');
                                              }
                                              setWorkspaceMenuAnchor(null); 
                                              setWorkspaceMenuId(null); 
                                            }}
                                            disabled={enrichedWorkspaces.length === 1}
                                            sx={{ fontSize: '14px', gap: 1, color: enrichedWorkspaces.length === 1 ? '#D0D5DD' : '#F04438' }}
                                          >
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} /> Delete
                                          </MenuItem>
                                        </Menu>
                                      </Box>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Box>
                )}

                {/* Workspace Details Sub-section (Full Page View) */}
                {workspaceSubSection === 'workspace-details' && selectedWorkspaceDetails && (
                  <Box>
                    {/* Back button */}
                    <Button
                      variant="text"
                      startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
                      onClick={() => { setWorkspaceSubSection('management'); setSelectedWorkspaceDetails(null); }}
                      sx={{ mb: 2, color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
                    >
                      Back to Workspaces
                    </Button>

                    {/* Workspace Header */}
                    <Card sx={{ mb: 3, borderRadius: '12px', border: '1px solid #E9EAEB', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Box sx={{ width: 80, height: 80, borderRadius: '12px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                              <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                            </svg>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '24px', color: '#101828', mb: 0.5 }}>{selectedWorkspaceDetails.name}</Typography>
                            <Typography sx={{ fontSize: '14px', color: '#667085', mb: 1 }}>Workspace ID: {selectedWorkspaceDetails.id}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                              <Chip label="Active" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontSize: '12px', fontWeight: 500 }} />
                              <Chip label={`${selectedWorkspaceDetails.users?.length || 0} members`} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '12px', fontWeight: 500 }} />
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#667085', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                              Last activity: {selectedWorkspaceDetails.lastActivityAt ? new Date(selectedWorkspaceDetails.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => openWorkspaceDetails(selectedWorkspaceDetails)}
                            sx={{ textTransform: 'none', borderColor: '#D0D5DD', color: '#344054', borderRadius: '8px', '&:hover': { borderColor: '#617BFF', bgcolor: '#F9FAFB' } }}
                          >
                            Edit
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Members */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>Members</Typography>
                      <IconButton
                        size="small"
                        onClick={() => openWorkspaceDetails(selectedWorkspaceDetails)}
                        sx={{ 
                          width: 28, 
                          height: 28, 
                          bgcolor: '#F0F2FF', 
                          color: '#617BFF', 
                          '&:hover': { bgcolor: '#E0E4FF' },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #E9EAEB', boxShadow: 'none' }}>
                      <div className="rounded-lg overflow-hidden">
                        <table className="w-full table-auto">
                          <thead>
                            <tr className="border-b border-gray-200" style={{ backgroundColor: '#F9FAFB', height: '44px' }}>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedWorkspaceDetails.users && selectedWorkspaceDetails.users.length > 0 ? (
                              selectedWorkspaceDetails.users.map((u: any) => (
                                <tr key={u.userId} className="hover:bg-gray-50 transition-all" style={{ height: '56px' }}>
                                  <td className="px-4 py-3">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Avatar src={u.user?.imageUrl || ''} alt={u.user?.name || u.user?.email} sx={{ width: 32, height: 32 }} />
                                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#101828' }}>{`${u.user?.name || ''} ${u.user?.lastName || ''}`.trim() || u.user?.email || '-'}</Typography>
                                    </Box>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Chip label={u.role} size="small" sx={{ bgcolor: u.role === 'ADMIN' ? '#F0F2FF' : '#F3F4F6', color: u.role === 'ADMIN' ? '#617BFF' : '#374151', fontSize: '11px', fontWeight: 500 }} />
                                  </td>
                                  <td className="px-4 py-3">
                                    <Typography sx={{ fontSize: '14px', color: '#667085' }}>{u.user?.email || '-'}</Typography>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={3} className="px-4 py-8 text-gray-500 text-center">No members in this workspace</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    {/* Policies Shortcut */}
                    <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 2, mt: 4 }}>Policies & Compliance</Typography>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #E9EAEB', boxShadow: 'none' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                              </svg>
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mb: 0.5 }}>AI Governance Policies</Typography>
                              <Typography sx={{ fontSize: '13px', color: '#667085' }}>View policies and violations for workspace "{selectedWorkspaceDetails.name}"</Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setActiveSection('Policies')}
                            sx={{ 
                              textTransform: 'none', 
                              borderColor: '#D0D5DD', 
                              color: '#344054', 
                              borderRadius: '8px',
                              fontWeight: 500,
                              '&:hover': { borderColor: '#617BFF', bgcolor: '#F9FAFB' }
                            }}
                          >
                            View Policies
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            )}

            {activeSection === 'Activation Links' && isFlashbackTeam && (
              <Box sx={{ p: 0 }}>
                <ActivationLinks />
              </Box>
            )}

            {/* Policies Section - LLM Guardrails */}
            {activeSection === 'Policies' && (
              <Box sx={{ p: 0 }}>
                <AIPolicies embedded />
              </Box>
            )}

            {/* Statistics Section */}
            {activeSection === 'Statistics' && (
              <Box sx={{ p: 0 }}>
                <StatisticsExplorer
                  orgName={org?.name || 'Organization'}
                  workspaces={globalData.workspaces || []}
                  repositories={statsRepositories}
                  bucketsCount={bucketsCount}
                  repositoriesCount={repositoriesCount}
                  storageUsed={`${quotaData?.storageUsed?.toFixed(1) || '32'} GB`}
                  storageLimit={quotaData?.displayLimit || '100 GB'}
                  team={team}
                  aiStats={getAggregatedAiStats()}
                  subSection={statisticsSubSection as 'gateway' | 'chat' | 'policies' | 'credits'}
                  onSubSectionChange={(s: any) => setStatisticsSubSection(s)}
                  apiClient={apiClient}
                  withTokenRefresh={withTokenRefresh}
                />
              </Box>
            )}

            {/* Billing Section (merged Plan + Billing) */}
            {activeSection === 'Billing' && (
          <Box
            sx={{
              p: { xs: 2, sm: 4 },
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {creditsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Row 1: Current Subscription (left with credit boxes) | Buy credit packs (right) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 4, mb: 4 }}>
              {/* LEFT - Current Subscription + Credit consumption boxes */}
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '18px' }, color: '#101828', mb: 2 }}>
                  Current Subscription
                </Typography>
                <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '2px solid #617BFF', minHeight: '120px', position: 'relative' }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 1, sm: 0 }, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '14px', sm: '16px' }, color: '#101828' }}>
                          {userQuota?.subscription?.name || currentSubscription?.name || 'Free Trial'}
                        </Typography>
                        <Chip label="Active" size="small" sx={{ height: 22, bgcolor: '#10B981', color: 'white', fontWeight: 600, fontSize: '12px' }} />
                        <Chip label={currentSubscription?.periodType || 'YEARLY'} size="small" sx={{ height: 22, borderRadius: '12px', bgcolor: '#F2F4F7', color: '#414651', fontWeight: 600, fontSize: '12px' }} />
                      </Box>
                      <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 700, color: '#101828' }}>
                        {(() => {
                          const planName = (userQuota?.subscription?.name || currentSubscription?.name || '').toLowerCase();
                          const isFree = planName.includes('free') || planName.includes('trial');
                          const price = isFree ? 0 : currentSubscription?.price || 300;
                          return `$${price}`;
                        })()}
                        <Typography component="span" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#6B7280', ml: 0.5 }}>
                          per {currentSubscription?.periodType === 'YEARLY' ? 'year' : 'month'}
                        </Typography>
                      </Typography>
                    </Box>
                    {currentSubscription?.dateFrom && currentSubscription?.dateTo && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '12px', color: '#717680', mb: 0.5 }}>Subscription Period</Typography>
                        <Typography sx={{ fontSize: '13px', color: '#535862' }}>
                          {new Date(currentSubscription.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(currentSubscription.dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Button variant="contained" size="small" onClick={() => setUpgradePlanModalOpen(true)} sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#617BFF', color: 'white', fontSize: '12px', fontWeight: 600, '&:hover': { backgroundColor: '#5A6FE8' } }}>
                        Upgrade
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                {/* Credit consumption small boxes */}
                {creditsMode && creditsBalance != null && !creditsLoading && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                    <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                      <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Available</Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#101828' }}>{creditsBalance.realtimeBalance.toLocaleString()}</Typography>
                      <Typography sx={{ fontSize: '11px', color: '#667085' }}>credits</Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                      <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Pending</Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#F59E0B' }}>{creditsBalance.pendingConsumption.toLocaleString()}</Typography>
                      <Typography sx={{ fontSize: '11px', color: '#667085' }}>credits</Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                      <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Storage</Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#617BFF' }}>{creditsBalance.breakdown?.storage?.toLocaleString() ?? 0}</Typography>
                      <Typography sx={{ fontSize: '11px', color: '#667085' }}>credits used</Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                      <Typography sx={{ fontSize: '11px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>AI</Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#8B5CF6' }}>{creditsBalance.breakdown?.ai?.toLocaleString() ?? 0}</Typography>
                      <Typography sx={{ fontSize: '11px', color: '#667085' }}>credits used</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* RIGHT - Buy credit packs */}
              {creditsMode && (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '18px' }, color: '#101828', mb: 2 }}>
                    Buy credit packs
                  </Typography>
                  <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #E9EAEB', p: 2 }}>
                    {creditPacks.length === 0 ? (
                      <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>No packs available.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {creditPacks.map((pack) => (
                          <Box key={pack.id} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', p: 2, minWidth: 140, textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>{pack.name}</Typography>
                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#101828' }}>{pack.credits} credits</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>${pack.price}</Typography>
                            <Button size="small" variant="contained" onClick={() => handleBuyCreditsPack(pack.id)} sx={{ mt: 1, textTransform: 'none', borderRadius: '8px', backgroundColor: '#617BFF', '&:hover': { backgroundColor: '#5A6FE8' } }}>
                              Buy
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Card>
                </Box>
              )}
            </Box>

            {/* Row 2: Credits Usage History - 12 Month Graph (replaces "Available credit") */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '16px', sm: '18px' },
                    color: '#101828',
                  }}
                >
                  Credits Usage History
                </Typography>
                {/* Toggles */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    component="label"
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: '#535862' }}
                  >
                    <Checkbox
                      size="small"
                      checked={histogramShowConsumption}
                      onChange={(e) => setHistogramShowConsumption(e.target.checked)}
                      sx={{ p: 0.5, color: '#EF4444', '&.Mui-checked': { color: '#EF4444' } }}
                    />
                    Consumption
                  </Box>
                  <Box
                    component="label"
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: '#535862' }}
                  >
                    <Checkbox
                      size="small"
                      checked={histogramShowPurchases}
                      onChange={(e) => setHistogramShowPurchases(e.target.checked)}
                      sx={{ p: 0.5, color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                    Purchases/Grants
                  </Box>
                  <Box
                    component="label"
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: '#535862' }}
                  >
                    <Checkbox
                      size="small"
                      checked={histogramShowBalance}
                      onChange={(e) => setHistogramShowBalance(e.target.checked)}
                      sx={{ p: 0.5, color: '#617BFF', '&.Mui-checked': { color: '#617BFF' } }}
                    />
                    Balance
                  </Box>
                </Box>
              </Box>
              <Card
                sx={{
                  borderRadius: '12px',
                  boxShadow: 'none',
                  border: '1px solid #E9EAEB',
                  p: 3,
                }}
              >
                {monthlyStatsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : monthlyStats.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>No data available</Typography>
                  </Box>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={monthlyStats.map((item) => ({
                          month: new Date(item.month).toLocaleDateString('en-US', { month: 'short' }),
                          consumption: item.consumption,
                          purchasesGrants: item.purchases + item.grants,
                          balance: item.balance,
                        }))}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E9EAEB" />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#667085' }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#667085' }} width={50} />
                        <RechartsTooltip
                          contentStyle={{ borderRadius: 8, border: '1px solid #E9EAEB', fontSize: 12 }}
                          formatter={(value: number, name: string) => [
                            value.toLocaleString(),
                            name === 'consumption' ? 'Consumption' : name === 'purchasesGrants' ? 'Purchases/Grants' : 'Balance',
                          ]}
                        />
                        {histogramShowConsumption && (
                          <Bar dataKey="consumption" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={16} />
                        )}
                        {histogramShowPurchases && (
                          <Bar dataKey="purchasesGrants" fill="#10B981" radius={[4, 4, 0, 0]} barSize={16} />
                        )}
                        {histogramShowBalance && (
                          <Bar dataKey="balance" fill="#617BFF" radius={[4, 4, 0, 0]} barSize={16} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: '1px solid #E9EAEB' }}>
                      <Box>
                        <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Total Consumption</Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#EF4444' }}>
                          {monthlyStatsTotals?.consumption.toLocaleString() ?? 0}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Purchases + Grants</Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#10B981' }}>
                          {((monthlyStatsTotals?.purchases ?? 0) + (monthlyStatsTotals?.grants ?? 0)).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '12px', color: '#667085', mb: 0.5 }}>Net Balance</Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#617BFF' }}>
                          {monthlyStatsTotals?.balance.toLocaleString() ?? 0}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Card>
            </Box>

            {/* Row 3: Credit Transactions - Full width */}
            {creditsMode && (
              <Box sx={{ mt: 4 }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '18px' }, color: '#101828', mb: 2 }}>
                  Transactions
                </Typography>
                <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #E9EAEB', minHeight: 200 }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ height: 44, bgcolor: '#FAFAFA', borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', px: 3 }}>
                      <Box sx={{ width: 130, flexShrink: 0 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Date</Typography></Box>
                      <Box sx={{ width: 90, flexShrink: 0 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Type</Typography></Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Description</Typography></Box>
                      <Box sx={{ width: 90, flexShrink: 0, textAlign: 'right' }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Amount</Typography></Box>
                    </Box>
                    <Box sx={{ minHeight: 156 }}>
                      {creditTransactions.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                          <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>No transactions yet</Typography>
                        </Box>
                      ) : (
                        creditTransactions.slice(0, 10).map((tx, index) => (
                          <Box
                            key={tx.id}
                            sx={{
                              height: 44,
                              display: 'flex',
                              alignItems: 'center',
                              px: 3,
                              borderBottom: index < Math.min(creditTransactions.length - 1, 9) ? '1px solid #E9EAEB' : 'none',
                            }}
                          >
                            <Box sx={{ width: 130, flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '13px', color: '#414651' }}>
                                {new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </Typography>
                            </Box>
                            <Box sx={{ width: 90, flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '12px', color: '#535862' }}>{tx.type}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <Typography sx={{ fontSize: '13px', color: '#414651' }} noWrap>
                                {tx.description ?? (tx.subscriptionName ? `Subscription: ${tx.subscriptionName}` : tx.packName ? `Pack: ${tx.packName}` : '—')}
                              </Typography>
                            </Box>
                            <Box sx={{ width: 90, flexShrink: 0, textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: tx.creditAmount >= 0 ? '#10B981' : '#101828' }}>
                                {tx.creditAmount >= 0 ? '+' : ''}{formatCreditAmount(tx.creditAmount)}
                              </Typography>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Row 4: Billing History - Full width + Open Portal button */}
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '18px' }, color: '#101828' }}>
                  Billing History
                </Typography>
                <Tooltip title="Manage your payment methods, invoices, and subscription details" placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenBillingPortal}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      borderColor: '#D0D5DD',
                      color: '#344054',
                      fontWeight: 600,
                      fontSize: '13px',
                      '&:hover': { borderColor: '#617BFF', bgcolor: '#F5F7FF', color: '#617BFF' },
                    }}
                  >
                    Open Portal
                  </Button>
                </Tooltip>
              </Box>
              <Card sx={{ borderRadius: '12px', boxShadow: 'none', bgcolor: 'white', border: '1px solid #E9EAEB', minHeight: '200px' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ height: '44px', bgcolor: '#FAFAFA', borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', px: 3 }}>
                    <Box sx={{ width: '160px' }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Date</Typography></Box>
                    <Box sx={{ width: '120px' }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Status</Typography></Box>
                    <Box sx={{ flex: 1, textAlign: 'right' }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Amount</Typography></Box>
                  </Box>
                  <Box sx={{ minHeight: '156px', display: 'flex', flexDirection: 'column' }}>
                    {paymentsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : payments.length > 0 ? (
                      payments.slice(0, 10).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((payment, index) => {
                        const statusConfig = formatPaymentStatus(payment.status);
                        return (
                          <Box
                            key={payment.id}
                            sx={{
                              height: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              px: 3,
                              borderBottom: index < Math.min(payments.length - 1, 9) ? '1px solid #E9EAEB' : 'none',
                            }}
                          >
                            <Box sx={{ width: '160px' }}>
                              <Typography sx={{ fontSize: '13px', color: '#414651' }}>
                                {new Date(payment.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '120px' }}>
                              <Chip label={statusConfig.label} size="small" sx={{ bgcolor: statusConfig.bgcolor, color: statusConfig.color, fontSize: '11px' }} />
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '13px', color: '#414651' }}>
                                {formatPrice(payment.amount, payment.currency)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>No payment history</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Upgrade Plan Modal */}
    <UpgradePlanModal
      open={upgradePlanModalOpen}
      onClose={() => setUpgradePlanModalOpen(false)}
      showStripeLinks={true}
      currentPlan={userQuota?.subscription?.name || currentSubscription?.name || undefined}
    />

    {/* Workspace Details Dialog */}
    <Dialog
      open={wsDetailsOpen}
      onClose={() => setWsDetailsOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { border: '1px solid #E9EAEB', borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
        Workspace details
      </DialogTitle>
      <DialogContent>
        {wsDetailsError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {wsDetailsError}
          </Alert>
        )}
        {wsDetailsLoading && <LinearProgress sx={{ mt: 1 }} />}
        {selectedWorkspaceForDetails && (
          <>
            <TextField
              label="Workspace name"
              value={wsNameEdit}
              onChange={(e) => setWsNameEdit(e.target.value)}
              fullWidth
              size="small"
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mt: 2 }}>
              <TextField
                label="Add user by email"
                value={addingEmail}
                onChange={(e) => setAddingEmail(e.target.value)}
                placeholder="user@example.com"
                size="small"
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
              />
              <FormControl sx={{ minWidth: { xs: '100%', sm: 140 } }} size="small">
                <InputLabel id="ws-add-role-label">Role</InputLabel>
                <Select
                  labelId="ws-add-role-label"
                  label="Role"
                  value={addingRole}
                  onChange={(e) => setAddingRole(e.target.value as WorkspaceTypes.AccessType)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                >
                    <MenuItem value={WorkspaceTypes.AccessType.READ}>READ</MenuItem>
                    <MenuItem value={WorkspaceTypes.AccessType.WRITE}>WRITE</MenuItem>
                    <MenuItem value={WorkspaceTypes.AccessType.ADMIN}>ADMIN</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleAddUserToWorkspace}
                disabled={savingWs}
                sx={{ bgcolor: '#617BFF', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '14px', borderRadius: '8px', height: '36px', width: { xs: '100%', sm: 'auto' }, '&:hover': { bgcolor: '#4A67E3' } }}
              >
                Add
              </Button>
            </Box>

            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#414651', mt: 3, mb: 1 }}>
              Members
            </Typography>
            <List dense sx={{ border: '1px solid #E9EAEB', borderRadius: '12px' }}>
              {((selectedWorkspaceForDetails as any).users || []).map((u: WorkspaceTypes.WorkspaceUser) => (
                <ListItem
                  key={u.userId}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveUserFromWorkspace(u.userId)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={`${u.user?.name || ''} ${u.user?.lastName || ''}`.trim() || u.user?.email || u.userId}
                    primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '14px', color: '#414651' } }}
                    secondary={
                      <>
                        <Chip size="small" label={u.role} sx={{ mr: 1 }} />
                        {u.user?.email}
                      </>
                    }
                    secondaryTypographyProps={{ sx: { fontWeight: 400, fontSize: '14px', color: '#717680' } }}
                  />
                </ListItem>
              ))}
              {(!selectedWorkspaceForDetails.users || selectedWorkspaceForDetails.users.length === 0) && (
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ color: '#717680', fontSize: '14px' }}>No members</Typography>
                </Box>
              )}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setWsDetailsOpen(false)}
          disabled={savingWs}
          sx={{ bgcolor: 'transparent', color: '#414651', textTransform: 'none', fontWeight: 600, fontSize: '14px', mr: 1, borderRadius: '8px', height: '36px' }}
        >
          Close
        </Button>
        <Button
          onClick={handleUpdateWsName}
          variant="contained"
          disabled={savingWs || !selectedWorkspaceForDetails || wsNameEdit.trim() === selectedWorkspaceForDetails?.name?.trim()}
          sx={{
            bgcolor: selectedWorkspaceForDetails && wsNameEdit.trim() !== selectedWorkspaceForDetails?.name?.trim() && !savingWs ? '#617BFF' : '#E5E7EB',
            color: selectedWorkspaceForDetails && wsNameEdit.trim() !== selectedWorkspaceForDetails?.name?.trim() && !savingWs ? 'white' : '#9CA3AF',
            textTransform: 'none', fontWeight: 600, fontSize: '14px', borderRadius: '8px', height: '36px',
            '&:hover': { bgcolor: selectedWorkspaceForDetails && wsNameEdit.trim() !== selectedWorkspaceForDetails?.name?.trim() && !savingWs ? '#4A67E3' : '#E5E7EB' },
            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
          }}
        >
          {savingWs ? 'Saving...' : 'Save name'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Edit Role Modal (Popup) - Flashback branded */}
    <Dialog
      open={editRoleModalOpen}
      onClose={() => setEditRoleModalOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { border: '1px solid #E9EAEB', borderRadius: '16px', overflow: 'hidden' } }}
    >
      {/* Branded header */}
      <Box sx={{ background: 'linear-gradient(135deg, #617BFF 0%, #4A67E3 100%)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: 'white', letterSpacing: '-0.01em' }}>
            Edit Member Role
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
            Update permissions for this team member
          </Typography>
        </Box>
        <IconButton onClick={() => setEditRoleModalOpen(false)} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </IconButton>
      </Box>
      <DialogContent sx={{ px: 3, py: 3 }}>
        {editRoleMember && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, borderRadius: '10px', bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
              <Avatar src={(editRoleMember as any).imageUrl || ''} alt={editRoleMember.name || editRoleMember.email} sx={{ width: 44, height: 44, border: '2px solid #E5E7EB' }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>{editRoleMember.name || '-'}</Typography>
                <Typography sx={{ fontSize: '13px', color: '#667085' }}>{editRoleMember.email}</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Role</Typography>
            <MultiRoleSelect value={editRoleValue} onChange={setEditRoleValue} />
          </Box>
        )}
      </DialogContent>
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #E9EAEB', display: 'flex', justifyContent: 'flex-end', gap: 1.5, bgcolor: '#FAFAFA' }}>
        <Button
          onClick={() => setEditRoleModalOpen(false)}
          sx={{ bgcolor: 'white', color: '#414651', textTransform: 'none', fontWeight: 600, fontSize: '14px', borderRadius: '8px', height: '36px', border: '1px solid #D0D5DD', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#9CA3AF' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={async () => {
            if (editRoleMember) {
              try {
                setTeamLoading(true);
                const updateUserData: OrganizationTypes.UpdateOrgUserRequest = { orgRole: editRoleValue };
                const res = await withTokenRefresh(() => apiClient.updateOrganizationUser(editRoleMember.id, updateUserData));
                if (res.success) {
                  const users = await loadUsersWithRoles();
                  setTeam(users);
                  setEditRoleModalOpen(false);
                  toast.success('Role updated successfully');
                }
              } catch (e: any) {
                setTeamError(e?.message || 'Failed to update role');
              } finally {
                setTeamLoading(false);
              }
            }
          }}
          disabled={teamLoading}
          sx={{ bgcolor: '#617BFF', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '14px', borderRadius: '8px', height: '36px', boxShadow: '0 1px 2px rgba(97,123,255,0.3)', '&:hover': { bgcolor: '#4A67E3' }, '&:disabled': { bgcolor: '#A4B4FF' } }}
        >
          {teamLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>

  </>
  );
}

export default Organization;
